/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import './App.css';
import { processUploadedFiles } from './documentUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── UPLOAD SIZE LIMITS ────────────────────────────────────────────────────
// The backend accepts a 25MB JSON body (express.json limit). PDFs are sent
// base64-encoded inside that JSON, and base64 inflates bytes by ~33%.
// So the real ceiling on the ORIGINAL files is about 25 / 1.34 = 18.6MB TOTAL,
// not per file.
//
// Exceeding it does NOT produce a clean 413. The connection is torn down
// mid-upload, which surfaces in the browser as an opaque network failure
// (ERR_SSL_BAD_RECORD_MAC_ALERT in Chrome, a null-status CORS error in
// Firefox). There is no server response to read, so this MUST be caught
// client-side before the request is made.
// NOTE: express.json is configured for 25MB, but that is NOT the binding limit.
// Something upstream at Render kills the connection on larger uploads, which is
// why an oversized request produces an opaque browser network error
// (ERR_SSL_BAD_RECORD_MAC_ALERT in Chrome, null-status CORS error in Firefox)
// instead of a clean 413 from our own server.
//
// Measured against production on 2026-09-02 (upload ~1.8MB/s):
//    4.1MB body  -> 500, full body received, ~2.1s   (x3, consistent)
//    6.8MB body  -> 500, full body received, ~3.8s
//    8.2MB body  -> 500, full body received, ~2.9s
//    9.5MB body  -> connection killed at 4.5MB after 1.7s
//   15.0MB body  -> connection killed at 10.2MB
// The cutoff point WANDERS, so this is not a clean byte limit — larger uploads
// are simply unreliable. 6MB of raw PDF (~8MB body) is the largest size that
// completed, so that is the ceiling until Render explains the behaviour.
//
// PROPER FIX (not yet done): send one document per request. Three 2MB requests
// each finish in ~1s and never approach the failure zone.
const MAX_TOTAL_UPLOAD_MB = 6;            // total across ALL documents, raw bytes
const MAX_SINGLE_FILE_MB  = 20;           // existing per-file guard, unchanged

const bytesToMB = b => b / (1024 * 1024);
const base64Size = bytes => Math.ceil(bytes / 3) * 4;   // encoded size in bytes

/**
 * Returns null if the batch is safe to send, or a user-facing message if not.
 */
function checkTotalUploadSize(files) {
  const rawBytes = files.reduce((s, f) => s + (f?.size || 0), 0);
  if (bytesToMB(rawBytes) <= MAX_TOTAL_UPLOAD_MB) return null;

  const rawMB = bytesToMB(rawBytes).toFixed(1);
  const names = files.map(f => `${f.name} (${bytesToMB(f.size).toFixed(1)}MB)`).join(', ');
  return `Your documents total ${rawMB}MB, which is over the ${MAX_TOTAL_UPLOAD_MB}MB combined limit. `
       + `Files: ${names}. `
       + `Try uploading the tax return on its own first, or re-export it as a smaller PDF `
       + `(in your tax software choose "Filing Copy Only", or print pages 1-20 to a new PDF).`;
}

function App() {
  const [file, setFile] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [redactorBannerDismissed, setRedactorBannerDismissed] = useState(false);
  const [w2Files, setW2Files] = useState([]); // W-2 upload
  const [paystubFiles, setPaystubFiles] = useState([]); // Multiple paystubs for forward planning
  const [paystubData, setPaystubData] = useState(null); // Extracted paystub data for confirmation
  const [editablePaystubData, setEditablePaystubData] = useState(null); // Editable copy
  // Multi-paystub state
  const [allExtractedPaystubs, setAllExtractedPaystubs] = useState([]);
  const [confirmedPaystubs, setConfirmedPaystubs] = useState([]);
  const [currentPaystubIndex, setCurrentPaystubIndex] = useState(0);
  const [paystubDuplicateWarning, setPaystubDuplicateWarning] = useState(null);
  // Non-W2 income confirmation state
  const [nonW2Items, setNonW2Items] = useState([]);
  const [confirmedNonW2, setConfirmedNonW2] = useState({});
  const [nonW2RawInputs, setNonW2RawInputs] = useState({});
  const [isSelfEmployed, setIsSelfEmployed] = useState(false);
  const [selfEmployedIncome, setSelfEmployedIncome] = useState({ month: '', ytdNetProfit: '' });
  const [showZeroSavings, setShowZeroSavings] = useState(false); // Show $0 items
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(''); // Custom loading message
  const [step, setStep] = useState('landing');
  const [warmingUp, setWarmingUp] = useState(false); // Cold start warning
  const warmupTimerRef = React.useRef(null);
  const extractedDataRef = React.useRef(null);

  // Scroll reveal for landing page
  useEffect(() => {
    if (step !== 'landing') return;
    const timer = setTimeout(() => {
      const els = document.querySelectorAll('.ts-reveal');
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); } });
      }, { threshold: 0.08 });
      els.forEach(el => io.observe(el));
      return () => io.disconnect();
    }, 100);
    return () => clearTimeout(timer);
  }, [step]);
  const [taxpayerBirthYear, setTaxpayerBirthYear] = useState('');
  const [spouseBirthYear, setSpouseBirthYear] = useState('');
  const [dependentBirthYears, setDependentBirthYears] = useState({}); // keyed by dependent index
  const [ageInputError, setAgeInputError] = useState('');
  const [selectedMode, setSelectedMode] = useState(null); // 'historical' or 'forward'
  const [extractedData, setExtractedData] = useState(null);

  // ── YEAR DERIVATION ──────────────────────────────────────────────────────
  // Every year shown in the UI must follow the uploaded return, not a literal.
  // Historical analysis = the return's own tax year.
  // Forward planning    = that year + 1 (matches the backend, which computes
  //                       planningYear = taxYear + 1).
  // Previously the forward-planning side was hardcoded to 2025 in 11 places,
  // so a 2025 return still offered "2025 Tax Planning" instead of 2026.
  const returnYear   = extractedData?.tax_year || null;
  const planningYear = returnYear ? returnYear + 1 : null;
  const yr  = returnYear   || '';          // historical, blank until known
  const pyr = planningYear || '';          // forward, blank until known
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [form1040Error, setForm1040Error] = useState(null);
  const [w2Error, setW2Error] = useState(null);
  const [paystubError, setPaystubError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.size > 20 * 1024 * 1024) {
      setForm1040Error('Your tax return file is too large (max 20MB). Try printing it as a PDF to reduce the file size.');
      setFile(null);
      return;
    }

    if (selectedFile.type === 'application/pdf') {
      // Check if PDF is password-protected by reading first bytes
      const reader = new FileReader();
      reader.onload = (evt) => {
        const bytes = new Uint8Array(evt.target.result);
        const header = String.fromCharCode(...bytes.slice(0, 1024));
        if (header.includes('/Encrypt')) {
          setForm1040Error(`Your tax return PDF is password-protected. To remove the password: open it in Chrome, press Ctrl+P (or Cmd+P on Mac), choose "Save as PDF" as the destination, and save. Then upload the new copy.`);
          setFile(null);
        } else {
          setFile(selectedFile);
          setForm1040Error(null);
          setError(null);
        }
      };
      reader.readAsArrayBuffer(selectedFile.slice(0, 1024));
    } else {
      const ext = selectedFile.name.split('.').pop().toUpperCase();
      setForm1040Error(`Please upload your tax return as a PDF, not a .${ext} file.`);
      setFile(null);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // NEW: W-2 file handlers
  const handleW2Change = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const pdfFiles = selectedFiles.filter(file => file.type === 'application/pdf');
    const nonPdfFiles = selectedFiles.filter(file => file.type !== 'application/pdf');

    if (nonPdfFiles.length > 0) {
      const exts = [...new Set(nonPdfFiles.map(f => f.name.split('.').pop().toUpperCase()))].join(', ');
      setW2Error(`Please upload your W-2 as a PDF, not a .${exts} file.`);
    }

    const oversized = pdfFiles.filter(f => f.size > 20 * 1024 * 1024);
    if (oversized.length > 0) {
      setW2Error(`Your W-2 file is too large (max 20MB). Please compress or re-export it.`);
      return;
    }

    const maxW2s = 10;
    if (pdfFiles.length > maxW2s) {
      setW2Error(`Maximum ${maxW2s} W-2s allowed. If you have more, combine them into one PDF.`);
      setW2Files(pdfFiles.slice(0, maxW2s));
    } else {
      setW2Files(pdfFiles);
      if (pdfFiles.length > 0 && nonPdfFiles.length === 0) setW2Error(null);
    }
  };

  const removeW2 = (index) => {
    setW2Files(prev => prev.filter((_, i) => i !== index));
  };

  // Paystub handler (supports multiple)
  const handlePaystubChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const pdfFiles = selectedFiles.filter(file => file.type === 'application/pdf');
    const nonPdfFiles = selectedFiles.filter(file => file.type !== 'application/pdf');

    if (nonPdfFiles.length > 0) {
      const exts = [...new Set(nonPdfFiles.map(f => f.name.split('.').pop().toUpperCase()))].join(', ');
      setPaystubError(`Please upload your paystub as a PDF, not a .${exts} file.`);
    }

    const oversized = pdfFiles.filter(f => f.size > 20 * 1024 * 1024);
    if (oversized.length > 0) {
      setPaystubError(`Your paystub file is too large (max 20MB). Please compress or re-export it.`);
      return;
    }

    const maxPaystubs = 5;
    if (pdfFiles.length > maxPaystubs) {
      setPaystubError(`Maximum ${maxPaystubs} paystubs allowed. Upload your most recent one for the most accurate projections.`);
      setPaystubFiles(pdfFiles.slice(0, maxPaystubs));
    } else {
      setPaystubFiles(pdfFiles);
      if (pdfFiles.length > 0 && nonPdfFiles.length === 0) setPaystubError(null);
    }
  };

  const removePaystub = (index) => {
    setPaystubFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    if (!file) {
      setError('Please upload your tax return PDF before analyzing.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Step 1: Extract tax data from PDF
      setStep('extracting');
      setWarmingUp(false);
      // Show cold start warning after 8 seconds if still waiting
      warmupTimerRef.current = setTimeout(() => setWarmingUp(true), 8000);
      
      // Collect all files to process
      const allFiles = [file, ...w2Files];
      
      console.log(`📤 Processing ${allFiles.length} documents with smart filtering...`);
      
      // ✅ NEW: Smart filter to remove bank statements and junk
      const processingResult = await processUploadedFiles(allFiles);
      
      // Show what was filtered
      if (processingResult.skippedFiles.length > 0) {
        console.log(`⏭️  Filtered out ${processingResult.skippedFiles.length} non-tax documents:`,
          processingResult.skippedFiles.map(d => d.filename));
      }
      
      // Check if we have any relevant documents
      if (processingResult.relevantFiles.length === 0) {
        const skippedNames = processingResult.skippedFiles.map(d => d.filename);
        const mainFileSkipped = skippedNames.includes(file.name);
        const w2sSkipped = w2Files.filter(f => skippedNames.includes(f.name));

        if (mainFileSkipped) {
          setForm1040Error(`The file you uploaded doesn't appear to be a tax return. Please upload your complete tax return PDF (Form 1040 + all schedules).`);
        }
        if (w2sSkipped.length > 0) {
          setW2Error(`${w2sSkipped.length > 1 ? 'Some of your' : 'The'} W-2 file${w2sSkipped.length > 1 ? 's' : ''} uploaded don't appear to be W-2 wage statements. Please upload your W-2 PDF.`);
        }
        setLoading(false);
        setStep('upload');
        return;
      }

      // ✅ Also check if W-2s were specifically skipped even though 1040 passed
      if (w2Files.length > 0) {
        const skippedNames = processingResult.skippedFiles.map(d => d.filename);
        const w2sSkipped = w2Files.filter(f => skippedNames.includes(f.name));
        if (w2sSkipped.length > 0) {
          setW2Error(`${w2sSkipped.length > 1 ? 'Some of your' : 'The'} W-2 file${w2sSkipped.length > 1 ? 's' : ''} uploaded don't appear to be W-2 wage statements. Please upload your W-2 PDF from your payroll portal.`);
          setLoading(false);
          setStep('upload');
          return;
        }

        // ✅ Backup: check filenames for obvious non-W-2 patterns
        const nonW2Patterns = ['stmt', 'statement', 'bank', 'invoice', 'receipt', 'check', 'balance', 'account', 'transaction', 'summary', '1099', 'schedule'];
        const suspiciousW2s = w2Files.filter(f => {
          const nameLower = f.name.toLowerCase();
          return nonW2Patterns.some(p => nameLower.includes(p));
        });
        if (suspiciousW2s.length > 0) {
          setW2Error(`The file "${suspiciousW2s[0].name}" doesn't appear to be a W-2. Please upload your W-2 wage statement PDF from your payroll portal (ADP, Workday, Paychex).`);
          setLoading(false);
          setStep('upload');
          return;
        }
      }
      
      console.log(`✅ Sending ${processingResult.relevantFiles.length} tax documents to backend`);
      
      // ✅ PRE-FLIGHT SIZE CHECK — must happen BEFORE the request is sent.
      // An oversized body kills the connection with no server response, so
      // there is nothing to catch afterwards. Fail here with a real message.
      const sizeProblem = checkTotalUploadSize(processingResult.relevantFiles);
      if (sizeProblem) {
        console.warn('⛔ Upload blocked before sending:', sizeProblem);
        setForm1040Error(sizeProblem);
        setLoading(false);
        setStep('upload');
        return;
      }

      // ✅ Convert filtered PDFs to base64
      const documents = [];
      for (const pdfFile of processingResult.relevantFiles) {
        const base64Data = await convertToBase64(pdfFile);
        documents.push(base64Data);
      }
      
      console.log(`📤 Sending ${documents.length} PDFs to backend`);

      // ✅ USE PROVEN ENDPOINT: /api/extract-tax-data
      const extractResponse = await fetch(`${API_BASE_URL}/api/extract-tax-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documents: documents
        }),
      });

      if (!extractResponse.ok) {
        // Read the specific error code from the server response
        let errData = {};
        try { errData = await extractResponse.json(); } catch (_) {}
        if (errData.error === 'pdf_too_many_pages') {
          throw new Error('pdf_too_many_pages');
        }
        if (errData.error === 'pdf_too_large') {
          throw new Error('pdf_too_large');
        }
        throw new Error(errData.message || errData.error || 'failed to extract tax data');
      }

      const extractResult = await extractResponse.json();
      
      if (!extractResult.success) {
        throw new Error(extractResult.error || 'Extraction failed');
      }

      // ✅ Validate extracted data quality — catch wrong documents BEFORE age-input
      const profile = extractResult.data?.taxpayer_profile;
      const hasAGI = profile?.agi > 0;
      const hasFilingStatus = !!profile?.filing_status && profile.filing_status !== 'unknown';
      const hasTaxableIncome = profile?.taxable_income !== undefined && profile?.taxable_income !== null;
      const hasTotalTax = profile?.total_tax !== undefined && profile?.total_tax !== null;

      if (!hasAGI && !hasFilingStatus && !hasTaxableIncome && !hasTotalTax) {
        setForm1040Error(`The file you uploaded doesn't appear to be a valid tax return. We couldn't find any tax data (AGI, filing status, or tax amounts). Please upload your complete Form 1040 PDF including all schedules.`);
        setStep('upload');
        setLoading(false);
        return;
      }

      if (!hasFilingStatus || !hasAGI) {
        setForm1040Error(`We could only partially read your tax return. Please make sure you're uploading the complete tax return PDF (Form 1040 + all schedules), not just a single page or schedule.`);
        setStep('upload');
        setLoading(false);
        return;
      }

      // ✅ SAME AS BEFORE: Store extracted data
      setExtractedData(extractResult.data);
      extractedDataRef.current = extractResult.data;
      console.log('✅ Extracted data:', extractResult.data);

      // Pre-populate age from checkbox if already 65+ (senior checkbox on 1040)
      if (profile?.taxpayer_age_2025 === 66) {
        setTaxpayerBirthYear('');
      }

      // Go to age input step before selection
      setStep('age-input');

    } catch (err) {
      console.error('Analysis error:', err);
      const msg = err.message || '';

      // NETWORK-LEVEL FAILURE — the request never completed, so there is no
      // server response and no error code to read. fetch() rejects with a bare
      // TypeError for every such case: oversized body, dropped connection,
      // offline, DNS failure. Previously this fell through to the generic
      // "try re-exporting your tax return" message, which sent people chasing
      // a PDF problem that did not exist.
      if (err instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(msg)) {
        const totalMB = bytesToMB([file, ...w2Files].reduce((s, f) => s + (f?.size || 0), 0)).toFixed(1);
        setForm1040Error(
          `We couldn't reach the server. Your documents total ${totalMB}MB — if that is close to ${MAX_TOTAL_UPLOAD_MB}MB, `
          + `the upload is likely too large. Try uploading the tax return by itself, or re-export it as a smaller PDF. `
          + `If the file is small, check your internet connection and try again.`
        );
        setStep('upload');
        setLoading(false);
        return;
      }

      // PDF too many pages (Anthropic 100-page limit)
      if (msg === 'pdf_too_many_pages' || msg.includes('maximum of 100 PDF pages')) {
        setForm1040Error(`Your PDF has too many pages (max: 100 pages). Your tax software likely included instruction booklets. Please re-export and select "Filing Copy Only", or print just Form 1040 + schedules to a new PDF (typically pages 1–20).`);
      // PDF too large in tokens (Anthropic context limit)
      } else if (msg === 'pdf_too_large' || msg.includes('exceed context limit')) {
        setForm1040Error(`Your PDF is too large to process. Please upload only the core tax return pages — Form 1040 + key schedules (typically pages 1–20). Remove instruction booklets, worksheets, or state returns and try again.`);
      // Password-protected PDF — most specific and actionable error
      } else if (msg.toLowerCase().includes('password') || msg.toLowerCase().includes('encrypt') ||
          msg.toLowerCase().includes('protected')) {
        setForm1040Error(`Your PDF is password-protected. Please remove the password before uploading: (1) Open in Chrome/Edge → Print → Save as PDF, (2) Mac: Preview → File → Export as PDF, or (3) Use smallpdf.com → Unlock PDF. Then re-upload.`);
      } else if (msg.toLowerCase().includes('failed to extract') || msg === '') {
        setForm1040Error(`We couldn't read your PDF. If it is password-protected, open it in Chrome, press Print, then Save as PDF to create an unlocked copy. If not password-protected, try re-exporting from your tax software.`);
      } else if (msg.toLowerCase().includes('1040') || msg.toLowerCase().includes('tax return')) {
        setForm1040Error(`We couldn't read your tax return. Please upload a complete tax return PDF (Form 1040 + all schedules).`);
      } else if (msg.toLowerCase().includes('w-2') || msg.toLowerCase().includes('w2')) {
        setW2Error(`We couldn't read your W-2. Please upload your W-2 PDF.`);
      } else {
        setForm1040Error(`We couldn't process your documents. Please try re-exporting your tax return as a PDF and upload again.`);
      }
      setStep('upload');
    } finally {
      setLoading(false);
      setWarmingUp(false);
      clearTimeout(warmupTimerRef.current);
    }
  };

  // NEW: Handle selection of analysis mode
  const handleModeSelection = async (mode) => {
    setSelectedMode(mode);
    
    // If forward planning — check SE first, then show appropriate screen
    if (mode === 'forward') {
      const activeData = extractedDataRef.current || extractedData;
      if (detectSelfEmployed(activeData)) {
        setIsSelfEmployed(true);
        setStep('se-income');
      } else {
        setStep('paystub-upload');
      }
      return;
    }
    
    // Otherwise run historical analysis
    setLoading(true);
    setStep('analyzing');

    try {
      const analyzeResponse = await fetch(`${API_BASE_URL}/api/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          extractedData: extractedDataRef.current || extractedData, // Use ref — always has latest ages
          showZeroSavings: showZeroSavings,
          tax_year: (extractedDataRef.current || extractedData)?.tax_year
        }),
      });

      if (!analyzeResponse.ok) {
        throw new Error('Failed to analyze tax data');
      }

      const analyzeResult = await analyzeResponse.json();
      setAnalysis(analyzeResult);
      console.log('Analysis result:', analyzeResult);

      setStep('results');

    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'An error occurred during analysis');
      setStep('selection');
    } finally {
      setLoading(false);
    }
  };

  // Handle forward planning analysis (with or without paystub)
  // Detect self-employed: Schedule C present AND no significant W-2 wages
  const detectSelfEmployed = (data) => {
    // Rule: SE = Schedule C income exists AND no W-2 wages
    // No Schedule C = not SE, regardless of everything else
    // (covers retirees, students, investors, W-2+side-gig)
    const schedC = data?.schedule_c?.net_profit
      || data?.schedule_c?.net
      || data?.schedule_c?.income
      || data?.business_income?.net_profit
      || data?.business_income?.net
      || data?.taxpayer_profile?.schedule_c_income
      || data?.taxpayer_profile?.self_employment_income
      || data?.taxpayer_profile?.business_income
      || data?.self_employment?.net_profit
      || data?.self_employment?.income
      || 0;
    const w2Wages = data?.w2_employment?.total_wages
      || data?.taxpayer_profile?.total_wages
      || data?.taxpayer_profile?.wages
      || data?.wages
      || (data?.w2_employment?.w2s || []).reduce((s, w) => s + (w.wages || 0), 0)
      || 0;

    const hasSchedC = Math.abs(schedC) > 500;
    const hasNoW2 = w2Wages < 5000;

    console.log('SE Detection:', JSON.stringify({ schedC, w2Wages, hasSchedC, hasNoW2, isSE: hasSchedC && hasNoW2 }));

    // ONLY true when Schedule C explicitly present AND no W-2 wages
    return hasSchedC && hasNoW2;
  };

  // Run self-employed forward planning with manually entered income
  const handleSelfEmployedAnalysis = async () => {
    const activeData = extractedDataRef.current || extractedData;
    const month = parseInt(selfEmployedIncome.month);
    const ytdNet = parseFloat(String(selfEmployedIncome.ytdNetProfit).replace(/,/g,''));
    if (!month || !ytdNet) return;
    const projectedAnnualNet = Math.round(ytdNet / month * 12);
    setLoading(true);
    setLoadingMessage(`Analyzing with ${(activeData?.tax_year || 2024) + 1} Tax Rules...`);
    setStep('analyzing');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/analyze-precise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          extractedData: activeData,
          paystubData: null,
          nonW2Income: { schedule_c: projectedAnnualNet },
          tax_year: (activeData?.tax_year || 2024) + 1,
          showZeroSavings: showZeroSavings,
          isSelfEmployedProjection: true
        }),
      });
      if (!resp.ok) throw new Error('Failed to analyze tax data');
      const result = await resp.json();
      setAnalysis(result);
      setStep('results');
    } catch (err) {
      setError(err.message || 'An error occurred');
      setStep('se-income');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleForwardPlanningAnalysis = async () => {
    const activeData = extractedDataRef.current || extractedData;
    // Self-employed → dedicated SE income entry screen
    if (detectSelfEmployed(activeData)) {
      setIsSelfEmployed(true);
      setStep('se-income');
      return;
    }
    // If no paystub, just run regular analysis
    if (paystubFiles.length === 0) {
      setLoading(true);
      setLoadingMessage(`Analyzing with ${extractedData?.tax_year || 'Current'} Tax Rules...`);
      setStep('analyzing');

      try {
        const analyzeResponse = await fetch(`${API_BASE_URL}/api/analyze`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            extractedData: extractedDataRef.current || extractedData, // Use ref — always has latest ages
            showZeroSavings: showZeroSavings,
            tax_year: extractedData?.tax_year
          }),
        });

        if (!analyzeResponse.ok) {
          throw new Error('Failed to analyze tax data');
        }

        const analyzeResult = await analyzeResponse.json();
        setAnalysis(analyzeResult);
        console.log('Forward planning result:', analyzeResult);

        setStep('results');

      } catch (err) {
        console.error('Forward planning error:', err);
        setError(err.message || 'An error occurred during forward planning');
        setStep('paystub-upload');
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
      return;
    }

    // Extract ALL uploaded paystubs sequentially
    setLoading(true);
    setError(null);
    setAllExtractedPaystubs([]);
    setConfirmedPaystubs([]);
    setCurrentPaystubIndex(0);
    setPaystubDuplicateWarning(null);
    setNonW2Items([]);
    setConfirmedNonW2({});
    setNonW2RawInputs({});
    setIsSelfEmployed(false);
    setSelfEmployedIncome({ month: '', ytdNetProfit: '' });

    const extracted = [];
    for (let i = 0; i < paystubFiles.length; i++) {
      setLoadingMessage(`Extracting paystub ${i + 1} of ${paystubFiles.length}...`);
      setStep('analyzing');
      try {
        const paystubBase64 = await convertToBase64(paystubFiles[i]);
        const resp = await fetch(`${API_BASE_URL}/api/extract-paystub`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paystub: paystubBase64 }),
        });
        if (!resp.ok) throw new Error(`Failed to extract paystub ${i + 1}`);
        const result = await resp.json();
        extracted.push({ ...result.paystubData, _index: i, _fileName: paystubFiles[i].name });
      } catch (err) {
        const msg = err.message || '';
        // Route to inline paystub error with friendly message
        if (msg.includes('Failed to extract') || msg.includes('validation failed') || msg.includes('missing required fields') || msg.includes('not a paystub')) {
          setPaystubError(`The file you uploaded doesn't appear to be a paystub. Please upload a YTD paystub PDF showing your year-to-date earnings and withholdings.`);
        } else if (msg.includes('future') || msg.includes('date')) {
          setPaystubError(`The paystub date looks incorrect. Please upload your most recent paystub.`);
        } else if (msg.includes('year') || msg.includes('2024') || msg.includes('2025')) {
          setPaystubError(`The paystub year doesn't match your tax return year. Please upload a paystub from the correct tax year.`);
        } else if (msg.includes('withholding') || msg.includes('gross')) {
          setPaystubError(`We couldn't read the earnings data from your paystub. Please make sure the PDF is not scanned or image-only.`);
        } else {
          setPaystubError(`We couldn't read your paystub. Please upload a clear PDF paystub showing your YTD earnings and withholdings.`);
        }
        setStep('paystub-upload');
        setLoading(false);
        setLoadingMessage('');
        return;
      }
    }

    // Auto-attribute each paystub to taxpayer or spouse using W2 employer + wage matching
    const w2s = extractedData?.w2_employment?.w2s || [];
    const isMFJInit = extractedData?.taxpayer_profile?.filing_status?.toLowerCase().includes('joint');
    const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const fuzzyMatch = (a, b) => { const na=normalize(a),nb=normalize(b); return !!(na&&nb&&(na.includes(nb)||nb.includes(na))); };
    const annualizeYTD = (ytdGross, payPeriodEnd) => {
      const d = new Date(payPeriodEnd || '');
      const month = d.getMonth() + 1 || 8;
      const day   = d.getDate() || 31;
      const periodsElapsed = Math.max(1, (month - 1) * 2 + (day <= 15 ? 1 : 2));
      return (ytdGross / periodsElapsed) * 24;
    };

    const attributed = extracted.map((ps) => {
      const psEmployer = ps.employer_name || '';
      const psAnnualized = annualizeYTD(ps.ytd_gross || 0, ps.pay_period_end);
      let owner = 'taxpayer'; // safe default
      if (isMFJInit && w2s.length > 1) {
        const employerMatchIdx = w2s.findIndex(w2 => fuzzyMatch(w2.employer, psEmployer));
        const wageDiffs = w2s.map((w2, i) => ({ i, diff: Math.abs((w2.wages || 0) - psAnnualized) }));
        wageDiffs.sort((a, b) => a.diff - b.diff);
        const wageMatchIdx = wageDiffs[0].diff < psAnnualized * 0.15 ? wageDiffs[0].i : -1;
        if (employerMatchIdx >= 0 && wageMatchIdx >= 0 && employerMatchIdx === wageMatchIdx) {
          owner = employerMatchIdx === 0 ? 'taxpayer' : 'spouse';
        } else if (wageMatchIdx >= 0) {
          owner = wageMatchIdx === 0 ? 'taxpayer' : 'spouse';
        } else if (employerMatchIdx >= 0) {
          owner = employerMatchIdx === 0 ? 'taxpayer' : 'spouse';
        }
      }
      return { ...ps, _owner: owner, _confirmed: false };
    });

    setAllExtractedPaystubs(attributed);
    setEditablePaystubData({ ...attributed[0] });
    setLoading(false);
    setLoadingMessage('');
    setStep('paystub-confirm');
  };

  // Detect non-W2 income items from 1040 extracted data
  const detectNonW2Items = (data) => {
    const items = [];
    const THRESHOLD = 500;
    const schedC = data?.schedule_c?.net_profit || data?.business_income?.net_profit || 0;
    if (Math.abs(schedC) > THRESHOLD) items.push({
      key: 'schedule_c', label: 'Schedule C — Business Net Profit',
      amount2024: schedC, type: 'profit',
      description: data?.schedule_c?.business_name || data?.business_income?.business_name || 'Self-Employment Business'
    });
    const schedE = data?.schedule_e?.total_net || data?.rental_properties?.total_net ||
                   data?.rental_income?.net_rental_income || 0;
    const schedEProperties = data?.schedule_e?.properties || data?.rental_properties?.properties || [];
    if (Math.abs(schedE) > THRESHOLD) items.push({
      key: 'schedule_e', label: 'Schedule E — Rental Properties',
      amount2024: schedE, type: 'rental',
      description: schedEProperties.length > 1
        ? `${schedEProperties.length} rental properties (net total)`
        : schedEProperties[0]?.address || 'Rental Property',
      properties: schedEProperties
    });
    const interest = data?.investment_income?.taxable_interest || 0;
    if (interest > THRESHOLD) items.push({
      key: 'interest', label: 'Taxable Interest (Schedule B)',
      amount2024: interest, type: 'investment',
      description: 'Bank/Investment Interest'
    });
    const dividends = data?.investment_income?.qualified_dividends || 0;
    if (dividends > THRESHOLD) items.push({
      key: 'dividends', label: 'Qualified Dividends',
      amount2024: dividends, type: 'investment',
      description: 'Investment Dividends'
    });
    const otherIncome = data?.other_income?.total || 0;
    if (Math.abs(otherIncome) > THRESHOLD) items.push({
      key: 'other_income', label: 'Other Income (1099-NEC / Misc)',
      amount2024: otherIncome, type: 'other',
      description: '1099-NEC or other miscellaneous income'
    });
    return items;
  };

  // Run precise analysis with combined paystub + confirmed non-W2 amounts
  const runPreciseAnalysis = async (confirmedStubs, nonW2Amounts, isMFJ, activeData) => {
    const combine = (stubs) => {
      if (stubs.length === 0) return null;
      const base = stubs.reduce((a, b) => new Date(a.pay_period_end) >= new Date(b.pay_period_end) ? a : b);
      return {
        pay_period_end:          base.pay_period_end,
        pay_frequency:           base.pay_frequency,
        ytd_gross:               stubs.reduce((s, p) => s + (p.ytd_gross || 0), 0),
        ytd_federal_withholding: stubs.reduce((s, p) => s + (p.ytd_federal_withholding || 0), 0),
        ytd_state_withholding:   stubs.reduce((s, p) => s + (p.ytd_state_withholding || 0), 0),
        ytd_401k:                stubs.reduce((s, p) => s + (p.ytd_401k || 0), 0),
        ytd_hsa:                 stubs.reduce((s, p) => s + (p.ytd_hsa || 0), 0),
        employer_name:           stubs.map(p => p.employer_name).filter(Boolean).join(' + '),
        confidence:              'high',
      };
    };
    const tpCombined = combine(confirmedStubs.filter(p => p._owner === 'taxpayer'));
    const spCombined = isMFJ ? combine(confirmedStubs.filter(p => p._owner === 'spouse')) : null;
    const householdPaystub = {
      ...(tpCombined || spCombined),
      ytd_gross:               (tpCombined?.ytd_gross || 0) + (spCombined?.ytd_gross || 0),
      ytd_federal_withholding: (tpCombined?.ytd_federal_withholding || 0) + (spCombined?.ytd_federal_withholding || 0),
      ytd_state_withholding:   (tpCombined?.ytd_state_withholding || 0) + (spCombined?.ytd_state_withholding || 0),
      ytd_401k:                (tpCombined?.ytd_401k || 0) + (spCombined?.ytd_401k || 0),
      ytd_hsa:                 (tpCombined?.ytd_hsa || 0) + (spCombined?.ytd_hsa || 0),
      employer_name:           [tpCombined?.employer_name, spCombined?.employer_name].filter(Boolean).join(' | '),
    };
    setLoading(true);
    setLoadingMessage(`Analyzing with ${(activeData?.tax_year || 2024) + 1} Tax Rules...`);
    setStep('analyzing');
    try {
      const analyzeResponse = await fetch(`${API_BASE_URL}/api/analyze-precise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          extractedData:   activeData,
          paystubData:     householdPaystub,
          nonW2Income:     nonW2Amounts,
          tax_year:        (activeData?.tax_year || 2024) + 1,
          showZeroSavings: showZeroSavings
        }),
      });
      if (!analyzeResponse.ok) throw new Error('Failed to analyze tax data');
      const analyzeResult = await analyzeResponse.json();
      setAnalysis(analyzeResult);
      setStep('results');
    } catch (err) {
      console.error('Precise analysis error:', err);
      setError(err.message || 'An error occurred during analysis');
      setStep('paystub-confirm');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Confirm current paystub — duplicate check, advance to next or go to non-W2 screen
  const handleConfirmPaystub = async () => {
    const current = { ...editablePaystubData };
    const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const fuzzyMatch = (a, b) => { const na=normalize(a),nb=normalize(b); return !!(na&&nb&&(na.includes(nb)||nb.includes(na))); };
    const activeData = extractedDataRef.current || extractedData;
    const isMFJ = activeData?.taxpayer_profile?.filing_status?.toLowerCase().includes('joint');

    // Duplicate check: same employer + same owner already confirmed
    const isDuplicate = confirmedPaystubs.some(cp =>
      fuzzyMatch(cp.employer_name, current.employer_name) && cp._owner === current._owner
    );
    if (isDuplicate) {
      setPaystubDuplicateWarning(
        `A paystub from "${current.employer_name}" for this ${current._owner === 'spouse' ? 'spouse' : 'taxpayer'} is already confirmed. ` +
        `Adding another would double-count income. Please remove this paystub or correct the employer name.`
      );
      return;
    }
    setPaystubDuplicateWarning(null);

    const newConfirmed = [...confirmedPaystubs, current];
    setConfirmedPaystubs(newConfirmed);
    const nextIndex = currentPaystubIndex + 1;

    if (nextIndex < allExtractedPaystubs.length) {
      // More paystubs to review
      setCurrentPaystubIndex(nextIndex);
      setEditablePaystubData({ ...allExtractedPaystubs[nextIndex] });
      return;
    }

    // All paystubs confirmed — check for non-W2 income
    const nonW2 = detectNonW2Items(activeData);
    if (nonW2.length > 0) {
      const initConfirmed = {};
      nonW2.forEach(item => { initConfirmed[item.key] = item.amount2024; });
      setNonW2Items(nonW2);
      setConfirmedNonW2(initConfirmed);
      setStep('non-w2-confirm');
      return;
    }

    // No non-W2 income — run analysis directly
    await runPreciseAnalysis(newConfirmed, {}, isMFJ, activeData);
  };

  // Age input handler — computes age as of Dec 31 of tax year
  // IRS rule: age = tax_year - birth_year (Jan 1 birthdays age up on Dec 31 of prior year,
  // but birth year alone is sufficient for 50/55 threshold calculations)
  const handleAgeSubmit = () => {
    setAgeInputError('');
    const taxYear = extractedData?.tax_year || 2024;
    const isMFJ = extractedData?.taxpayer_profile?.filing_status?.toLowerCase().includes('joint');

    // Validate taxpayer birth year
    const tpYear = parseInt(taxpayerBirthYear);
    if (!taxpayerBirthYear || isNaN(tpYear) || tpYear < 1900 || tpYear > taxYear - 18) {
      setAgeInputError('Please enter a valid taxpayer birth year (e.g. 1975)');
      return;
    }

    // Validate spouse birth year if MFJ
    if (isMFJ) {
      const spYear = parseInt(spouseBirthYear);
      if (!spouseBirthYear || isNaN(spYear) || spYear < 1900 || spYear > taxYear - 18) {
        setAgeInputError('Please enter a valid spouse birth year (e.g. 1978)');
        return;
      }
    }

    // Compute ages as of Dec 31 of tax year
    const taxpayerAge = taxYear - tpYear;
    const spouseAge = isMFJ ? taxYear - parseInt(spouseBirthYear) : null;

    // Inject computed ages into extractedData — override whatever Claude extracted
    const updatedDependents = (extractedData.taxpayer_profile?.dependents || []).map((dep, idx) => {
      const byStr = dependentBirthYears[idx];
      const by = parseInt(byStr);
      if (byStr && !isNaN(by) && by > 1900) {
        return { ...dep, birth_year: by, age_2025: taxYear - by };
      }
      return dep;
    });

    const updatedData = {
      ...extractedData,
      taxpayer_profile: {
        ...extractedData.taxpayer_profile,
        taxpayer_age_2025: taxpayerAge,
        ...(spouseAge !== null && { spouse_age_2025: spouseAge }),
        taxpayer_birth_year: tpYear,
        ...(isMFJ && { spouse_birth_year: parseInt(spouseBirthYear) }),
        dependents: updatedDependents
      }
    };

    setExtractedData(updatedData);
    extractedDataRef.current = updatedData; // Keep ref in sync — avoids async state timing issue
    setStep('selection');
  };

  const handleReset = () => {
    setFile(null);
    setW2Files([]);
    setPaystubFiles([]);
    setPaystubData(null);
    setSelectedMode(null);
    setExtractedData(null);
    setAnalysis(null);
    setError(null);
    setTaxpayerBirthYear('');
    setSpouseBirthYear('');
    setDependentBirthYears({});
    setAgeInputError('');
    setAllExtractedPaystubs([]);
    setConfirmedPaystubs([]);
    setCurrentPaystubIndex(0);
    setPaystubDuplicateWarning(null);
    setNonW2Items([]);
    setConfirmedNonW2({});
    setNonW2RawInputs({});
    setIsSelfEmployed(false);
    setSelfEmployedIncome({ month: '', ytdNetProfit: '' });
    setStep('upload');
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#ca8a04';
      case 'low': return '#65a30d';
      default: return '#6b7280';
    }
  };

  const getConfidenceColor = (confidence) => {
    const conf = confidence?.toLowerCase();
    if (conf?.includes('very high')) return '#059669';
    if (conf?.includes('high')) return '#0891b2';
    if (conf?.includes('medium')) return '#d97706';
    if (conf?.includes('low') || conf?.includes('review')) return '#dc2626';
    return '#6b7280';
  };

  return (
    <div className="App">

      {/* ============================================================ */}
      {/* LANDING PAGE                                                  */}
      {/* ============================================================ */}
      {step === 'landing' && (
        <div id="ts-landing" style={{ minHeight: '100vh', fontFamily: "'Segoe UI', system-ui, sans-serif", overflowX: 'hidden', background: '#fff' }}>

          {/* GLOBAL STYLES — first child so keyframes defined before use */}
          <style>{`
            #ts-landing * { box-sizing: border-box; }
            @keyframes b1 { 0%,100%{transform:translate(0,0) scale(1)} 25%{transform:translate(100px,70px) scale(1.08)} 50%{transform:translate(50px,130px) scale(0.94)} 75%{transform:translate(-70px,60px) scale(1.05)} }
            @keyframes b2 { 0%,100%{transform:translate(0,0) scale(1)} 25%{transform:translate(-90px,55px) scale(1.1)} 55%{transform:translate(-55px,110px) scale(0.91)} 80%{transform:translate(75px,40px) scale(1.06)} }
            @keyframes b3 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(90px,-70px) scale(1.1)} 66%{transform:translate(-55px,-40px) scale(0.9)} }
            @keyframes b4 { 0%,100%{transform:translate(0,0) scale(1)} 40%{transform:translate(-70px,-55px) scale(1.08)} 70%{transform:translate(55px,-85px) scale(0.93)} }
            @keyframes b5 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-85px,70px) scale(1.14)} }
            @keyframes floatBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
            @keyframes pulseDot { 0%,100%{box-shadow:0 0 0 0 rgba(99,102,241,0.5);transform:scale(1)} 60%{box-shadow:0 0 0 9px rgba(99,102,241,0);transform:scale(1.25)} }
            @keyframes shimmer { 0%{left:-100%} 100%{left:100%} }
            @keyframes fadeUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
            @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
            @keyframes stepGlow { 0%,100%{box-shadow:0 0 0 0 rgba(99,102,241,0.25)} 50%{box-shadow:0 0 0 10px rgba(99,102,241,0)} }
            @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }

            .ts-reveal { opacity:0; transform:translateY(36px); transition:opacity 0.75s ease, transform 0.75s ease; }
            .ts-reveal.visible { opacity:1; transform:translateY(0); }

            .ts-card { transition:transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease; }
            .ts-card:hover { transform:translateY(-6px); box-shadow:0 20px 48px rgba(99,102,241,0.14) !important; border-color:rgba(99,102,241,0.3) !important; }

            .ts-stat { transition:all 0.3s ease; }
            .ts-stat:hover { transform:translateY(-5px) scale(1.03); box-shadow:0 12px 32px rgba(99,102,241,0.18) !important; }

            .ts-pill { transition:all 0.2s ease; cursor:default; }
            .ts-pill:hover { background:rgba(255,255,255,0.18) !important; color:#fff !important; }

            .ts-cta-btn { position:relative; overflow:hidden; transition:all 0.25s ease; }
            .ts-cta-btn::after { content:''; position:absolute; top:0; left:-100%; width:60%; height:100%; background:linear-gradient(90deg,transparent,rgba(255,255,255,0.25),transparent); animation:shimmer 2.8s ease-in-out infinite; }
            .ts-cta-btn:hover { transform:translateY(-3px) scale(1.02); }
          `}</style>

          {/* ── NAV ── */}
          <nav style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'0 56px', height:'76px', background:'#1e293b',
            position:'sticky', top:0, zIndex:100,
            borderBottom:'1px solid rgba(255,255,255,0.1)',
            boxShadow:'0 2px 24px rgba(0,0,0,0.25)'
          }}>
            <div style={{display:'flex',alignItems:'center',gap:'14px',cursor:'pointer'}}>
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <defs><linearGradient id="lg1" x1="0" y1="0" x2="44" y2="44"><stop offset="0%" stopColor="#2563eb"/><stop offset="100%" stopColor="#7c3aed"/></linearGradient></defs>
                <rect width="44" height="44" rx="12" fill="url(#lg1)"/>
                <path d="M22 7L33 12.5L33 24C33 29.5 27.5 34 22 36C16.5 34 11 29.5 11 24L11 12.5Z" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.28)" strokeWidth="0.8"/>
                <text x="22" y="29" textAnchor="middle" fill="white" fontSize="17" fontWeight="900" fontFamily="system-ui">$</text>
                {/* T crossbar — thin, close to $ */}
                <rect x="17.5" y="13.5" width="9" height="1.6" rx="0.8" fill="white"/>
                {/* Subtle AI badge */}
                <rect x="32" y="8" width="9" height="5" rx="2" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5"/>
                <text x="36.5" y="12" textAnchor="middle" fill="white" fontSize="2.8" fontWeight="700" fontFamily="system-ui">AI</text>
              </svg>
              <div>
                <div style={{fontSize:'22px',fontWeight:'800',color:'#fff',letterSpacing:'-0.5px',lineHeight:1.15}}>
                  TaxSavvy<span style={{color:'#4ade80'}}>.ai</span>
                  <sup style={{fontSize:'8px',color:'rgba(255,255,255,0.35)',fontWeight:'500',marginLeft:'3px'}}>TM</sup>
                </div>
                <div style={{fontSize:'10px',color:'rgba(255,255,255,0.35)',letterSpacing:'2px',textTransform:'uppercase',fontWeight:'600'}}>by AutoFin Systems</div>
              </div>
            </div>

            <div style={{display:'flex',gap:'10px'}}>
              {[['🛡️','CPA-Verified'],['🤖','AI-Powered'],['🔒','Data Never Stored']].map(([icon,label]) => (
                <div key={label} className="ts-pill" style={{
                  display:'flex',alignItems:'center',gap:'7px',
                  background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.18)',
                  borderRadius:'100px',padding:'8px 18px',
                  fontSize:'14px',color:'rgba(255,255,255,0.85)',fontWeight:'600'
                }}>
                  <span>{icon}</span>{label}
                </div>
              ))}
            </div>

            <button className="ts-cta-btn" onClick={() => setStep('upload')} style={{
              background:'linear-gradient(135deg,#22c55e,#16a34a)',
              color:'white',border:'none',borderRadius:'100px',
              padding:'13px 28px',fontSize:'15px',fontWeight:'700',
              cursor:'pointer',boxShadow:'0 0 24px rgba(34,197,94,0.4)'
            }}>Analyze My Taxes →</button>
          </nav>

          {/* ── HERO + PAIN ── */}
          <section style={{
            position:'relative',overflow:'hidden',
            background:'#eef2ff',
            padding:'60px 24px 80px',
          }}>
            {/* Animated blobs */}
            {[
              {w:900,h:900,t:'-280px',l:'-180px',bg:'radial-gradient(circle,rgba(99,102,241,0.35) 0%,rgba(99,102,241,0.08) 45%,transparent 70%)',a:'b1 20s ease-in-out infinite'},
              {w:800,h:800,t:'-180px',r:'-220px',bg:'radial-gradient(circle,rgba(139,92,246,0.3) 0%,rgba(139,92,246,0.07) 45%,transparent 70%)',a:'b2 25s ease-in-out infinite'},
              {w:700,h:700,b:'-180px',l:'5%',bg:'radial-gradient(circle,rgba(59,130,246,0.25) 0%,rgba(59,130,246,0.06) 45%,transparent 70%)',a:'b3 18s ease-in-out infinite'},
              {w:600,h:600,b:'-100px',r:'5%',bg:'radial-gradient(circle,rgba(16,185,129,0.2) 0%,rgba(16,185,129,0.05) 45%,transparent 70%)',a:'b4 22s ease-in-out infinite'},
            ].map((b,i) => (
              <div key={i} style={{
                position:'absolute',width:b.w,height:b.h,
                top:b.t,bottom:b.b,left:b.l,right:b.r,
                borderRadius:'50%',background:b.bg,
                animation:b.a,willChange:'transform',pointerEvents:'none'
              }}/>
            ))}

            {/* 2-column layout */}
            <div style={{
              position:'relative',zIndex:1,
              maxWidth:'1200px',margin:'0 auto',
              display:'grid',gridTemplateColumns:'1fr 1fr',
              gap:'40px',alignItems:'stretch'
            }}>

              {/* LEFT — Hero */}
              <div>
                <div style={{
                  display:'inline-flex',alignItems:'center',gap:'9px',
                  background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.25)',
                  borderRadius:'100px',padding:'9px 22px',marginBottom:'28px',
                  fontSize:'14px',color:'#4338ca',fontWeight:'700',
                  animation:'fadeUp 0.8s ease both'
                }}>
                  <span style={{width:'8px',height:'8px',borderRadius:'50%',background:'#6366f1',display:'inline-block',animation:'pulseDot 2.5s ease-in-out infinite'}}/>
                  AI-Powered · Built by CPAs · Personalized For You
                </div>

                <h1 style={{fontSize:'clamp(40px,5vw,72px)',fontWeight:'800',lineHeight:'1.04',letterSpacing:'-3px',color:'#1e1b4b',animation:'fadeUp 0.8s ease 0.1s both',margin:'0 0 4px'}}>
                  Don't Overpay
                </h1>
                <h1 style={{
                  fontSize:'clamp(40px,5vw,72px)',fontWeight:'800',lineHeight:'1.04',letterSpacing:'-3px',margin:'0 0 24px',
                  background:'linear-gradient(135deg,#6366f1 0%,#8b5cf6 45%,#06b6d4 100%)',
                  WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',backgroundClip:'text',
                  animation:'fadeUp 0.8s ease 0.18s both'
                }}>Your Taxes.</h1>

                <p style={{fontSize:'clamp(15px,1.6vw,18px)',color:'#4b5563',maxWidth:'480px',margin:'0 0 32px',lineHeight:'1.75',animation:'fadeUp 0.8s ease 0.28s both'}}>
                  The power of AI meets the expertise of CPAs — uncovering every deduction, credit, and savings opportunity in your return.{' '}
                  <strong style={{color:'#1e1b4b',fontWeight:'700'}}>Your actionable savings plan, delivered instantly.</strong>
                </p>

                <div style={{animation:'fadeUp 0.8s ease 0.38s both'}}>
                  <button className="ts-cta-btn" onClick={() => setStep('upload')} style={{
                    background:'linear-gradient(135deg,#22c55e 0%,#16a34a 100%)',
                    color:'white',border:'none',borderRadius:'16px',
                    padding:'18px 48px',fontSize:'17px',fontWeight:'700',
                    cursor:'pointer',boxShadow:'0 12px 40px rgba(34,197,94,0.4)',
                    display:'block',marginBottom:'12px'
                  }}>Analyze My Tax Return →</button>
                  <p style={{color:'#9ca3af',fontSize:'13px',margin:0,textAlign:'left'}}>Upload your 2024 or 2025 tax return · Results in minutes</p>
                </div>

                {/* Stat cards */}
                <div style={{display:'flex',gap:'12px',marginTop:'32px',flexWrap:'wrap'}}>
                  {[
                    {icon:'💸',val:'Thousands',label:'In Overpaid Taxes Found',delay:'0.5s'},
                    {icon:'⚡',val:'< 2 min',label:'To Your Savings Plan',delay:'0.58s'},
                    {icon:'🛡️',val:'100%',label:'CPA-Verified Rules',delay:'0.66s'},
                    {icon:'🔒',val:'0',label:'Data Ever Stored',delay:'0.74s'},
                  ].map((s,i) => (
                    <div key={i} className="ts-stat" style={{
                      textAlign:'center',padding:'14px 18px',minWidth:'120px',
                      background:'rgba(255,255,255,0.78)',border:'1px solid rgba(99,102,241,0.18)',
                      borderRadius:'14px',backdropFilter:'blur(14px)',
                      boxShadow:'0 4px 20px rgba(99,102,241,0.09)',
                      animation:`fadeUp 0.8s ease ${s.delay} both`
                    }}>
                      <div style={{fontSize:'18px',marginBottom:'4px'}}>{s.icon}</div>
                      <div style={{fontSize:'20px',fontWeight:'800',color:'#4338ca',letterSpacing:'-0.5px'}}>{s.val}</div>
                      <div style={{fontSize:'10px',color:'#6b7280',marginTop:'2px',textTransform:'uppercase',letterSpacing:'0.8px',fontWeight:'600'}}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT — Pain card */}
              <div style={{animation:'fadeUp 0.8s ease 0.3s both',display:'flex'}}>
                <div style={{
                  background:'rgba(255,255,255,0.82)',
                  border:'1px solid rgba(99,102,241,0.2)',
                  borderRadius:'24px',padding:'48px 40px',textAlign:'center',
                  position:'relative',overflow:'hidden',
                  backdropFilter:'blur(14px)',
                  boxShadow:'0 24px 60px rgba(99,102,241,0.15)',
                  flex:1,display:'flex',flexDirection:'column',justifyContent:'center'
                }}>
                  <div style={{position:'absolute',top:'-60px',right:'-60px',width:'300px',height:'300px',background:'radial-gradient(circle,rgba(99,102,241,0.12) 0%,transparent 70%)',pointerEvents:'none'}}/>
                  <div style={{fontSize:'44px',marginBottom:'16px',display:'inline-block',animation:'floatBob 4s ease-in-out infinite',position:'relative'}}>💸</div>
                  <h2 style={{fontSize:'clamp(22px,3vw,32px)',fontWeight:'800',letterSpacing:'-1px',color:'#1e1b4b',marginBottom:'16px',position:'relative'}}>
                    The IRS Won't Tell You<br/>What You Missed.
                  </h2>
                  <p style={{fontSize:'15px',color:'#4b5563',lineHeight:'1.8',margin:'0 auto 16px',position:'relative'}}>
                    The average American overpays by <strong style={{color:'#6366f1'}}>thousands of dollars every year</strong> — because the tax code has 70,000 pages of rules most people never see.
                  </p>
                  <p style={{fontSize:'15px',color:'#4b5563',lineHeight:'1.8',margin:'0 auto 20px',position:'relative'}}>
                    No individual can read them all. No accountant applies every rule to every client. Nobody is proactively hunting for every dollar you're entitled to keep.
                  </p>
                  <p style={{fontSize:'20px',fontWeight:'800',color:'#16a34a',margin:0,position:'relative'}}>Until now.</p>
                </div>
              </div>

            </div>

            {/* Wave */}
            <div style={{position:'absolute',bottom:-1,left:0,right:0,height:'70px',zIndex:2}}>
              <svg viewBox="0 0 1440 70" preserveAspectRatio="none" style={{width:'100%',height:'100%'}}>
                <path d="M0,35 C480,70 960,0 1440,35 L1440,70 L0,70 Z" fill="#fff"/>
              </svg>
            </div>
          </section>

          {/* ── TICKER ── */}
          <div style={{background:'#6366f1',padding:'14px 0',overflow:'hidden',whiteSpace:'nowrap'}}>
            <div style={{display:'inline-flex',animation:'ticker 40s linear infinite',gap:'0'}}>
              {[...Array(2)].map((_,rep) => (
                <span key={rep} style={{display:'inline-flex',gap:'56px',paddingRight:'56px'}}>
                  {['✦ AI-Powered Analysis','✦ Built by CPAs','✦ Personalized For You','✦ Historical Tax Analysis','✦ Future Tax Planning','✦ Data Never Stored','✦ Results in Minutes','✦ IRS-Compliant Rules'].map(t => (
                    <span key={t} style={{color:'rgba(255,255,255,0.88)',fontSize:'14px',fontWeight:'600',display:'inline-block'}}>{t}</span>
                  ))}
                </span>
              ))}
            </div>
          </div>

          {/* ── PRIVACY BAR ── */}
          <div style={{background:'#166534',padding:'14px 24px'}}>
            <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:'32px',flexWrap:'wrap',maxWidth:'900px',margin:'0 auto'}}>
              {[
                {icon:'🔒', text:'Your name & SSN stripped before analysis'},
                {icon:'🚫', text:'Documents deleted immediately after'},
                {icon:'✅', text:'Nothing ever stored on our servers'},
              ].map(item => (
                <div key={item.text} style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  <span style={{fontSize:'16px'}}>{item.icon}</span>
                  <span style={{fontSize:'14px',color:'#bbf7d0',fontWeight:'600'}}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── PRIVACY REDACTOR ── */}
          <section id="redactor" style={{background:'#0f172a',padding:'64px 24px'}}>
            <div style={{maxWidth:'920px',margin:'0 auto'}}>

              {/* Badge */}
              <div style={{textAlign:'center',marginBottom:'28px'}}>
                <div style={{display:'inline-flex',alignItems:'center',gap:'8px',background:'rgba(74,222,128,0.12)',border:'1px solid rgba(74,222,128,0.25)',borderRadius:'100px',padding:'7px 20px'}}>
                  <span style={{fontSize:'13px'}}>🔒</span>
                  <span style={{fontSize:'12px',color:'#4ade80',fontWeight:'700',letterSpacing:'0.8px'}}>OPTIONAL — FREE PRIVACY TOOL</span>
                </div>
              </div>

              {/* Headline */}
              <h2 style={{fontSize:'clamp(26px,4vw,40px)',fontWeight:'800',color:'#fff',letterSpacing:'-1px',margin:'0 0 16px',textAlign:'center'}}>
                PDF Privacy Redactor
              </h2>
              <p style={{fontSize:'16px',color:'rgba(255,255,255,0.6)',maxWidth:'620px',margin:'0 auto 48px',lineHeight:'1.8',textAlign:'center'}}>
                <strong style={{color:'#4ade80'}}>TaxSavvy.ai already protects your data automatically.</strong>
                {' '}But if you want extra peace of mind, this free tool lets you remove personal information from your PDF <em>before</em> you upload — right on your own computer.
              </p>

              {/* How it works — 3 steps */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:'16px',marginBottom:'40px'}}>
                {[
                  {num:'01',icon:'⬇️',title:'Download the app',desc:'One-click download. No installation wizard, no signup, no credit card.'},
                  {num:'02',icon:'📄',title:'Open your tax PDF',desc:'Select your Form 1040, W-2, or paystub. The app runs entirely on your computer.'},
                  {num:'03',icon:'✅',title:'Get a clean copy',desc:'A redacted PDF is saved next to your original. Names and SSN removed, all numbers kept.'},
                ].map(s => (
                  <div key={s.num} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'14px',padding:'24px'}}>
                    <div style={{fontSize:'11px',fontWeight:'800',color:'rgba(255,255,255,0.25)',letterSpacing:'2px',marginBottom:'12px'}}>STEP {s.num}</div>
                    <div style={{fontSize:'24px',marginBottom:'10px'}}>{s.icon}</div>
                    <div style={{fontWeight:'700',color:'#fff',fontSize:'15px',marginBottom:'8px'}}>{s.title}</div>
                    <div style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',lineHeight:'1.65'}}>{s.desc}</div>
                  </div>
                ))}
              </div>

              {/* What it removes vs keeps */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px',marginBottom:'40px'}}>
                <div style={{background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.15)',borderRadius:'14px',padding:'22px'}}>
                  <div style={{fontWeight:'700',color:'#fca5a5',fontSize:'14px',marginBottom:'14px'}}>🚫 What gets removed</div>
                  {['Your full name','Social Security Number (SSN)','Home address','Employee address on W-2','Dependent names'].map(i => (
                    <div key={i} style={{fontSize:'13px',color:'rgba(255,255,255,0.55)',padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.05)'}}>{i}</div>
                  ))}
                </div>
                <div style={{background:'rgba(74,222,128,0.06)',border:'1px solid rgba(74,222,128,0.15)',borderRadius:'14px',padding:'22px'}}>
                  <div style={{fontWeight:'700',color:'#4ade80',fontSize:'14px',marginBottom:'14px'}}>✅ What stays (needed for analysis)</div>
                  {['All income & wage amounts','Deductions & credits','Employer name & EIN','Tax paid & refund amounts','Filing status & tax year'].map(i => (
                    <div key={i} style={{fontSize:'13px',color:'rgba(255,255,255,0.55)',padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.05)'}}>{i}</div>
                  ))}
                </div>
              </div>

              {/* Trust points */}
              <div style={{display:'flex',flexWrap:'wrap',gap:'10px',justifyContent:'center',marginBottom:'40px'}}>
                {[
                  '🖥️ Runs only on your computer',
                  '📡 No internet connection used',
                  '📄 Original PDF never modified',
                  '👁️ See exactly what is removed',
                  '🆓 Free forever, no account needed',
                  '🗑️ Uninstall by deleting the file',
                ].map(t => (
                  <div key={t} style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'100px',padding:'7px 16px',fontSize:'13px',color:'rgba(255,255,255,0.6)'}}>{t}</div>
                ))}
              </div>

              {/* Download button */}
              <div style={{textAlign:'center',marginBottom:'28px'}}>
                <a href="/downloads/taxsavvy_redactor.py" download style={{
                  display:'inline-flex',alignItems:'center',gap:'12px',
                  background:'#6366f1',color:'white',
                  padding:'18px 40px',borderRadius:'14px',
                  textDecoration:'none',fontWeight:'700',fontSize:'17px',
                  boxShadow:'0 8px 24px rgba(99,102,241,0.35)'
                }}>
                  <span style={{fontSize:'24px'}}>⬇️</span>
                  <div style={{textAlign:'left'}}>
                    <div>Download Privacy Redactor</div>
                    <div style={{fontSize:'12px',opacity:0.7,fontWeight:'400'}}>taxsavvy_redactor.py · Free · Works on Windows &amp; Mac</div>
                  </div>
                </a>
              </div>

              {/* Setup instructions */}
              <div style={{maxWidth:'680px',margin:'0 auto 24px',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'14px',padding:'28px 32px'}}>
                <div style={{fontWeight:'700',color:'#fff',fontSize:'15px',marginBottom:'20px'}}>📋 Setup instructions — takes about 3 minutes</div>

                {/* Windows */}
                <div style={{marginBottom:'24px'}}>
                  <div style={{fontWeight:'700',color:'#a5b4fc',fontSize:'14px',marginBottom:'12px'}}>🪟 Windows</div>
                  {[
                    {n:'1',t:'Install Python (free, one-time)',d:'Go to python.org/downloads → click "Download Python" → run the installer. Important: check the box that says "Add Python to PATH" before clicking Install.'},
                    {n:'2',t:'Install the PDF library (one-time)',d:'Press the Windows key, type "cmd", press Enter. In the black window that opens, type:  pip install pymupdf  and press Enter. Wait for it to finish.'},
                    {n:'3',t:'Run the redactor',d:'Double-click taxsavvy_redactor.py — the TaxSavvy Redactor window opens. Click Browse, select your tax PDF, click Redact PDF.'},
                    {n:'4',t:'Get your redacted file',d:'A new file is saved in the same folder as your original, with "_redacted" added to the name. Upload that file to TaxSavvy.ai.'},
                  ].map(s => (
                    <div key={s.n} style={{display:'flex',gap:'14px',marginBottom:'12px'}}>
                      <div style={{minWidth:'22px',height:'22px',borderRadius:'50%',background:'rgba(99,102,241,0.3)',border:'1px solid rgba(99,102,241,0.5)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'700',color:'#a5b4fc',flexShrink:0,marginTop:'1px'}}>{s.n}</div>
                      <div>
                        <div style={{fontWeight:'600',color:'rgba(255,255,255,0.8)',fontSize:'13px',marginBottom:'3px'}}>{s.t}</div>
                        <div style={{fontSize:'12px',color:'rgba(255,255,255,0.45)',lineHeight:'1.65'}}>{s.d}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mac */}
                <div style={{borderTop:'1px solid rgba(255,255,255,0.07)',paddingTop:'20px'}}>
                  <div style={{fontWeight:'700',color:'#a5b4fc',fontSize:'14px',marginBottom:'12px'}}>🍎 Mac</div>
                  {[
                    {n:'1',t:'Install Python (free, one-time)',d:'Go to python.org/downloads → click "Download Python" → run the installer and follow the prompts.'},
                    {n:'2',t:'Install the PDF library (one-time)',d:'Open Terminal (press Cmd+Space, type "Terminal", press Enter). Type:  pip3 install pymupdf  and press Enter. Wait for it to finish.'},
                    {n:'3',t:'Run the redactor',d:'Double-click taxsavvy_redactor.py — the TaxSavvy Redactor window opens. Click Browse, select your tax PDF, click Redact PDF.'},
                    {n:'4',t:'Get your redacted file',d:'A new file is saved in the same folder as your original, with "_redacted" added to the name. Upload that file to TaxSavvy.ai.'},
                  ].map(s => (
                    <div key={s.n} style={{display:'flex',gap:'14px',marginBottom:'12px'}}>
                      <div style={{minWidth:'22px',height:'22px',borderRadius:'50%',background:'rgba(99,102,241,0.3)',border:'1px solid rgba(99,102,241,0.5)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'700',color:'#a5b4fc',flexShrink:0,marginTop:'1px'}}>{s.n}</div>
                      <div>
                        <div style={{fontWeight:'600',color:'rgba(255,255,255,0.8)',fontSize:'13px',marginBottom:'3px'}}>{s.t}</div>
                        <div style={{fontSize:'12px',color:'rgba(255,255,255,0.45)',lineHeight:'1.65'}}>{s.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p style={{color:'rgba(255,255,255,0.2)',fontSize:'12px',textAlign:'center',marginTop:'8px'}}>
                Free forever · No signup · Works on Windows 10/11 and macOS 12+ · Python is safe and used by millions
              </p>
            </div>
          </section>

          {/* ── TRUST + ARCHITECTURE ── */}
          <section className="ts-reveal" style={{background:'#f8fafc',padding:'60px 24px 60px'}}>
            <div style={{maxWidth:'1100px',margin:'0 auto'}}>

              {/* Header */}
              <div style={{textAlign:'center',marginBottom:'32px'}}>
                <p style={{color:'#6366f1',fontSize:'13px',textTransform:'uppercase',letterSpacing:'3px',fontWeight:'700',marginBottom:'8px'}}>Your Privacy Comes First</p>
                <h2 style={{fontSize:'clamp(24px,3vw,36px)',fontWeight:'800',letterSpacing:'-1px',color:'#1e1b4b',margin:'0 0 10px'}}>
                  Built on Trust. Designed for Security.
                </h2>
                <p style={{color:'#6b7280',fontSize:'15px',maxWidth:'500px',margin:'0 auto 12px',lineHeight:'1.6'}}>
                  Your tax return contains sensitive information. Here's exactly how we handle it.
                </p>
                <a href="/security.html" target="_blank" rel="noopener noreferrer" style={{
                  display:'inline-flex',alignItems:'center',gap:'6px',
                  background:'#f0fdf4',border:'1px solid #86efac',
                  borderRadius:'100px',padding:'6px 16px',
                  fontSize:'13px',color:'#16a34a',fontWeight:'600',
                  textDecoration:'none',transition:'all 0.2s'
                }}>
                  🔒 Read our full Data Protection page →
                </a>
              </div>

              {/* Trust cards — 4 in a row */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:'14px',marginBottom:'32px'}}>
                {[
                  {icon:'🛡️',title:'Personal Data Stripped First',desc:'Your name, SSN, address and employer details are removed before any analysis happens.'},
                  {icon:'🚫',title:'Never Stored. Ever.',desc:'Documents discarded the moment analysis completes. No database. Nothing retained.'},
                  {icon:'🔐',title:'Encrypted in Transit',desc:'Bank-level TLS encryption protects your data from your browser to our servers.'},
                  {icon:'🏛️',title:'IRS-Compliant Rules',desc:'Every recommendation based on current IRS rules, updated for 2024 and 2025 tax years.'},
                ].map((t,i) => (
                  <div key={i} className="ts-card" style={{
                    background:'#fff',border:'1px solid #e5e7eb',
                    borderRadius:'14px',padding:'20px',
                    boxShadow:'0 2px 8px rgba(0,0,0,0.04)'
                  }}>
                    <div style={{fontSize:'24px',marginBottom:'10px'}}>{t.icon}</div>
                    <h3 style={{fontSize:'14px',fontWeight:'700',color:'#1e1b4b',marginBottom:'6px',lineHeight:'1.3'}}>{t.title}</h3>
                    <p style={{color:'#6b7280',fontSize:'13px',lineHeight:'1.65',margin:0}}>{t.desc}</p>
                  </div>
                ))}
              </div>

              {/* Architecture Diagram */}
              <div style={{background:'linear-gradient(135deg,#1e1b4b,#312e81)',borderRadius:'20px',padding:'36px 32px',color:'white'}}>
                <div style={{textAlign:'center',marginBottom:'28px'}}>
                  <p style={{color:'rgba(255,255,255,0.5)',fontSize:'11px',textTransform:'uppercase',letterSpacing:'3px',fontWeight:'700',marginBottom:'6px'}}>How It Works Under the Hood</p>
                  <h3 style={{fontSize:'clamp(18px,2.5vw,26px)',fontWeight:'800',letterSpacing:'-0.5px',margin:0}}>
                    Your Data Never Leaves Without Being Cleaned First
                  </h3>
                </div>

                <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0',flexWrap:'wrap',rowGap:'16px'}}>
                  {[
                    {icon:'📄',title:'You Upload',sub:'Tax return PDF\nvia encrypted connection',color:'rgba(255,255,255,0.1)',border:'rgba(255,255,255,0.2)'},
                    {icon:'✂️',title:'PII Stripped',sub:'Name · SSN · Address\nremoved instantly',color:'rgba(239,68,68,0.2)',border:'rgba(239,68,68,0.4)',strikethrough:true},
                    {icon:'🧠',title:'AI + CPA Analysis',sub:'Only financial figures\nrun against 60+ tax rules',color:'rgba(99,102,241,0.3)',border:'rgba(99,102,241,0.5)'},
                    {icon:'💰',title:'Savings Plan',sub:'Returned to your browser\ninstantly',color:'rgba(34,197,94,0.2)',border:'rgba(34,197,94,0.4)'},
                    {icon:'🗑️',title:'Permanently Deleted',sub:'All data discarded\nnothing ever stored',color:'rgba(245,158,11,0.2)',border:'rgba(245,158,11,0.4)'},
                  ].map((step, i) => (
                    <React.Fragment key={i}>
                      <div style={{textAlign:'center',minWidth:'130px'}}>
                        <div style={{width:'64px',height:'64px',borderRadius:'16px',background:step.color,border:`1px solid ${step.border}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'28px',margin:'0 auto 10px'}}>
                          {step.icon}
                        </div>
                        <div style={{fontSize:'12px',fontWeight:'700',color:'white',marginBottom:'4px'}}>{step.title}</div>
                        <div style={{fontSize:'11px',color:'rgba(255,255,255,0.5)',lineHeight:'1.5',whiteSpace:'pre-line'}}>
                          {step.strikethrough
                            ? <><span style={{color:'#fca5a5',textDecoration:'line-through'}}>Name · SSN · Address</span>{'\n'}removed instantly</>
                            : step.sub}
                        </div>
                      </div>
                      {i < 4 && <div style={{color:'rgba(255,255,255,0.3)',fontSize:'20px',padding:'0 8px',marginBottom:'20px'}}>→</div>}
                    </React.Fragment>
                  ))}
                </div>

                <div style={{marginTop:'28px',paddingTop:'20px',borderTop:'1px solid rgba(255,255,255,0.1)',display:'flex',justifyContent:'center',gap:'24px',flexWrap:'wrap'}}>
                  {['🔒 Bank-Level TLS Encryption','🚫 Zero Data Retention','🛡️ PII Auto-Stripped','👨‍💼 CPA-Verified Engine'].map(b => (
                    <div key={b} style={{fontSize:'12px',color:'rgba(255,255,255,0.45)',fontWeight:'600'}}>{b}</div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── HOW IT WORKS ── */}
          <section style={{maxWidth:'1100px',margin:'0 auto',padding:'0 24px 100px'}}>
            <div className="ts-reveal" style={{textAlign:'center',marginBottom:'60px'}}>
              <p style={{color:'#6366f1',fontSize:'13px',textTransform:'uppercase',letterSpacing:'3px',fontWeight:'700',marginBottom:'12px'}}>Simple Process</p>
              <h2 style={{fontSize:'clamp(30px,4vw,48px)',fontWeight:'800',letterSpacing:'-1.5px',color:'#1e1b4b',margin:0}}>Your Savings Plan in 3 Steps</h2>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:'24px'}}>
              {[
                {num:'01',icon:'📄',title:'Upload Your Tax Return',desc:'Securely upload your Form 1040 and supporting documents. Analyzed instantly. Never stored, ever.',color:'#ede9fe',delay:'0s'},
                {num:'02',icon:'🧠',title:'AI + CPA Analysis',desc:'Analyzes your past return for missed savings AND models your future year — historical review and forward tax planning in one engine.',color:'#dbeafe',delay:'0.12s'},
                {num:'03',icon:'💰',title:'Get Your Savings Plan',desc:'Clear, prioritized list of what to do, how much you save, and exactly how — in plain English.',color:'#dcfce7',delay:'0.24s'},
              ].map((s,i) => (
                <div key={i} className="ts-card ts-reveal" style={{
                  background:'#fff',border:'1px solid #e5e7eb',borderRadius:'22px',padding:'40px',
                  boxShadow:'0 4px 20px rgba(0,0,0,0.05)',position:'relative',
                  transitionDelay:s.delay
                }}>
                  <div style={{display:'inline-flex',width:'52px',height:'52px',borderRadius:'14px',background:s.color,alignItems:'center',justifyContent:'center',fontSize:'26px',marginBottom:'22px',animation:'stepGlow 3s ease-in-out infinite',animationDelay:`${i*0.9}s`}}>{s.icon}</div>
                  <div style={{position:'absolute',top:'24px',right:'28px',fontSize:'52px',fontWeight:'900',color:'#f3f4f6',lineHeight:1}}>{s.num}</div>
                  <h3 style={{fontSize:'19px',fontWeight:'700',color:'#111827',marginBottom:'12px',letterSpacing:'-0.3px'}}>{s.title}</h3>
                  <p style={{color:'#6b7280',fontSize:'15px',lineHeight:'1.75',margin:0}}>{s.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── FEATURES ── */}
          <section style={{background:'#f9fafb',padding:'100px 24px'}}>
            <div style={{maxWidth:'1100px',margin:'0 auto'}}>
              <div className="ts-reveal" style={{textAlign:'center',marginBottom:'60px'}}>
                <p style={{color:'#6366f1',fontSize:'13px',textTransform:'uppercase',letterSpacing:'3px',fontWeight:'700',marginBottom:'12px'}}>Why TaxSavvy.ai</p>
                <h2 style={{fontSize:'clamp(30px,4vw,48px)',fontWeight:'800',letterSpacing:'-1.5px',color:'#1e1b4b',margin:0}}>Everything a CPA Knows.<br/>In Minutes.</h2>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:'20px'}}>
                {[
                  {icon:'🎯',title:'Comprehensive Analysis',desc:'From retirement gaps to energy credits, SALT optimization to investment deductions — every rule, every dollar.'},
                  {icon:'⚡',title:'Results in Under 2 Minutes',desc:'No appointment. No waiting. Upload and get your personalized savings plan instantly.'},
                  {icon:'🧬',title:'Tailored For You',desc:'Not generic advice. Your numbers, your filing status, your life — analyzed specifically for you.'},
                  {icon:'🔒',title:'Your Privacy, Guaranteed',desc:'Documents analyzed in real time, immediately discarded. Zero data stored. Zero risk. Ever.'},
                  {icon:'🔮',title:'Historical & Future Planning',desc:'Analyze what you already filed AND plan ahead for next year — both historical review and forward tax planning in one place.'},
                  {icon:'👨‍💼',title:'Built by CPAs',desc:'Every rule, every calculation grounded in real CPA expertise — not generic AI guesswork.'},
                ].map((f,i) => (
                  <div key={i} className="ts-card ts-reveal" style={{
                    background:'#fff',border:'1px solid #e5e7eb',borderRadius:'18px',padding:'30px',
                    boxShadow:'0 2px 10px rgba(0,0,0,0.04)',transitionDelay:`${i*0.07}s`
                  }}>
                    <div style={{fontSize:'30px',marginBottom:'16px'}}>{f.icon}</div>
                    <h3 style={{fontSize:'17px',fontWeight:'700',color:'#111827',marginBottom:'10px',letterSpacing:'-0.2px'}}>{f.title}</h3>
                    <p style={{color:'#6b7280',fontSize:'14px',lineHeight:'1.75',margin:0}}>{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── VIDEO ── */}
          <section style={{background:'#f9fafb',padding:'100px 24px',textAlign:'center'}}>
            <div className="ts-reveal" style={{maxWidth:'880px',margin:'0 auto'}}>
              <p style={{color:'#6366f1',fontSize:'13px',textTransform:'uppercase',letterSpacing:'3px',fontWeight:'700',marginBottom:'12px'}}>See It In Action</p>
              <h2 style={{fontSize:'clamp(28px,4vw,44px)',fontWeight:'800',letterSpacing:'-1.5px',color:'#1e1b4b',marginBottom:'40px'}}>Watch TaxSavvy.ai Find Real Savings</h2>
              <div style={{borderRadius:'24px',overflow:'hidden',boxShadow:'0 24px 60px rgba(99,102,241,0.2)',border:'1px solid #e0e7ff'}}>
                <iframe
                  src="/explainer.html"
                  style={{width:'100%',height:'520px',border:'none',display:'block'}}
                  title="TaxSavvy.ai — How It Works"
                />
              </div>
              <p style={{color:'#9ca3af',fontSize:'13px',marginTop:'16px'}}>
                Use arrow keys or click the dots to navigate · Press space to pause
              </p>
            </div>
          </section>

          {/* ── BOTTOM CTA ── */}
          <section className="ts-reveal" style={{background:'linear-gradient(135deg,#6366f1 0%,#7c3aed 100%)',padding:'100px 24px 120px',textAlign:'center'}}>
            <h2 style={{fontSize:'clamp(36px,5vw,64px)',fontWeight:'800',letterSpacing:'-2.5px',color:'#fff',marginBottom:'16px'}}>Your Tax Savings Are Waiting.</h2>
            <p style={{fontSize:'19px',color:'rgba(255,255,255,0.72)',maxWidth:'480px',margin:'0 auto 48px',lineHeight:'1.65'}}>
              Stop overpaying. Start keeping more of what you've already earned.
            </p>
            <button className="ts-cta-btn" onClick={() => setStep('upload')} style={{
              background:'#fff',color:'#6366f1',border:'none',borderRadius:'16px',
              padding:'22px 60px',fontSize:'20px',fontWeight:'800',
              cursor:'pointer',display:'block',margin:'0 auto 18px',
              boxShadow:'0 12px 40px rgba(0,0,0,0.18)'
            }}>Get My Tax Savings Analysis →</button>
            <p style={{color:'rgba(255,255,255,0.42)',fontSize:'13px',margin:0}}>Your data is analyzed in real time and never stored</p>
          </section>

          {/* ── FOOTER ── */}
          <footer style={{background:'#0f172a',padding:'32px 56px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'16px'}}>
            <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
              <div style={{width:'30px',height:'30px',borderRadius:'8px',background:'linear-gradient(135deg,#2563eb,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:'14px',fontWeight:'900'}}>T</div>
              <span style={{fontSize:'13px',color:'rgba(255,255,255,0.35)'}}>© 2026 AutoFin Systems Inc. | TaxSavvy.ai<sup>TM</sup> | Built by CPAs, Powered by AI</span>
            </div>
            <div style={{display:'flex',gap:'24px'}}>
              {[
                {label:'Privacy Policy', href:'/privacy.html'},
                {label:'Terms of Service', href:'/terms.html'},
                {label:'Data Protection', href:'/security.html'},
                {label:'Contact', href:'mailto:hello@taxsavvy.ai'},
              ].map(({label, href}) => (
                <a key={label} href={href}
                  target={href.startsWith('mailto') ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  style={{color:'rgba(255,255,255,0.4)',fontSize:'13px',cursor:'pointer',textDecoration:'none',transition:'color 0.2s'}}
                  onMouseOver={e => e.target.style.color='rgba(255,255,255,0.8)'}
                  onMouseOut={e => e.target.style.color='rgba(255,255,255,0.4)'}
                >{label}</a>
              ))}
            </div>
          </footer>
        </div>
      )}

      {/* ============================================================ */}
      {step !== 'landing' && (
    <div className="App" style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '32px', padding: '0 4px'
        }}>
          {/* Logo — click to go back to landing */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
            onClick={() => setStep('landing')}>
            <svg width="40" height="40" viewBox="0 0 200 200" fill="none">
              <defs>
                <linearGradient id="appLg" x1="0" y1="0" x2="200" y2="200" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#2563eb"/>
                  <stop offset="100%" stopColor="#7c3aed"/>
                </linearGradient>
              </defs>
              <rect width="200" height="200" rx="44" fill="url(#appLg)"/>
              <path d="M100 32 L158 60 L158 112 C158 142 130 164 100 174 C70 164 42 142 42 112 L42 60 Z"
                fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.28)" strokeWidth="2"/>
              <text x="100" y="132" textAnchor="middle" fill="white" fontSize="78" fontWeight="900"
                fontFamily="system-ui">$</text>
              <rect x="70" y="60" width="60" height="9" rx="4" fill="white"/>
              <rect x="148" y="36" width="36" height="20" rx="8" fill="rgba(255,255,255,0.2)"
                stroke="rgba(255,255,255,0.4)" strokeWidth="1"/>
              <text x="166" y="50" textAnchor="middle" fill="white" fontSize="14" fontWeight="700"
                fontFamily="system-ui">AI</text>
            </svg>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '800', color: 'white', letterSpacing: '-0.5px' }}>
                TaxSavvy<span style={{ color: '#4ade80' }}>.ai</span>
                <sup style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)', fontWeight: '500', marginLeft: '2px' }}>TM</sup>
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                AI-Powered Tax Savings
              </div>
            </div>
          </div>

          {/* Trust pills */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {[['🛡️', 'PII Stripped'], ['🔒', 'Never Stored'], ['👨‍💼', 'CPA-Verified']].map(([icon, label]) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '100px', padding: '6px 14px',
                fontSize: '12px', color: 'rgba(255,255,255,0.85)', fontWeight: '600'
              }}>
                <span>{icon}</span>{label}
              </div>
            ))}
          </div>
        </div>

        {/* Main Card */}
        <div style={{
          background: step === 'results' ? 'transparent' : 'white',
          borderRadius: '16px',
          boxShadow: step === 'results' ? 'none' : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          padding: step === 'results' ? '0' : '40px',
          marginBottom: '20px'
        }}>
          {step === 'upload' && (
            <>
              {/* Compelling header */}
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: 'linear-gradient(135deg, #ede9fe, #dbeafe)',
                  border: '1px solid #c7d2fe', borderRadius: '100px',
                  padding: '6px 18px', marginBottom: '14px',
                  fontSize: '13px', color: '#4338ca', fontWeight: '600'
                }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#6366f1', display: 'inline-block' }}/>
                  Step 1 of 3 — Upload
                </div>
                <h2 style={{
                  fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: '800',
                  color: '#1e1b4b', letterSpacing: '-0.5px', margin: '0 0 8px'
                }}>
                  Let's Find Your Hidden Tax Savings
                </h2>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                  Upload your tax return below — we'll do the rest.
                </p>
              </div>

              {/* ── Optional privacy banner — dismissible ── */}
              {!redactorBannerDismissed && (
                <div style={{
                  background:'linear-gradient(135deg,#f0fdf4,#dcfce7)',
                  border:'1px solid #bbf7d0', borderRadius:'12px',
                  padding:'12px 16px', marginBottom:'16px',
                  display:'flex', alignItems:'center', gap:'12px'
                }}>
                  <span style={{fontSize:'20px',flexShrink:0}}>🔒</span>
                  <div style={{flex:1,fontSize:'13px',color:'#166534',lineHeight:'1.6'}}>
                    <strong>Your privacy is already protected.</strong> We automatically strip your name and SSN before AI analysis — your personal details never reach the AI.
                    {' '}Want to remove them yourself first?{' '}
                    <a
                      href="/#redactor"
                      onClick={e => {
                        e.preventDefault();
                        setStep('landing');
                        setTimeout(() => {
                          const el = document.getElementById('redactor');
                          if (el) el.scrollIntoView({behavior:'smooth'});
                        }, 80);
                      }}
                      style={{color:'#15803d',fontWeight:'700',textDecoration:'underline',cursor:'pointer'}}
                    >
                      See our free Privacy Redactor
                    </a>
                    {' '}— runs on your computer, nothing uploaded. <em>(Optional)</em>
                  </div>
                  <button onClick={() => setRedactorBannerDismissed(true)} style={{
                    background:'none',border:'none',cursor:'pointer',
                    color:'#166534',fontSize:'18px',padding:'0 4px',flexShrink:0,opacity:0.6
                  }}>×</button>
                </div>
              )}

              {/* Error — always at top, impossible to miss */}
              {error && (
                <div style={{
                  background: '#fffbeb', border: '1px solid #f59e0b',
                  borderLeft: '5px solid #f59e0b', borderRadius: '10px',
                  padding: '12px 16px', marginBottom: '16px',
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  boxShadow: '0 4px 12px rgba(245,158,11,0.15)'
                }}>
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', color: '#92400e', fontSize: '13px', marginBottom: '2px' }}>Action Required</div>
                    <div style={{ color: '#92400e', fontSize: '13px', lineHeight: '1.5' }}>{error.replace('⚠️ ', '')}</div>
                  </div>
                  <button onClick={() => setError(null)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#92400e', fontSize: '18px', padding: '0 4px', flexShrink: 0
                  }}>×</button>
                </div>
              )}

              {/* 2-column layout */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>

                {/* LEFT — Form 1040 + W-2 uploads */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                  {/* Form 1040 */}
                  <div style={{
                    border: file ? '2px solid #6366f1' : '2px dashed #cbd5e1',
                    borderRadius: '14px', padding: '18px',
                    background: file ? '#f5f3ff' : '#f8fafc',
                    transition: 'all 0.2s'
                  }}>
                    <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '14px', marginBottom: '4px' }}>
                      📄 Tax Return <span style={{ color: '#ef4444', fontSize: '12px' }}>Required</span>
                    </div>
                    <p style={{ color: '#9ca3af', fontSize: '11px', margin: '0 0 8px' }}>
                      PDF format only · 6MB total across all documents
                    </p>
                    <input
                      type="file" accept=".pdf" onChange={handleFileChange}
                      style={{ display: 'block', width: '100%', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}
                    />
                    {file ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#ede9fe', borderRadius: '8px', padding: '6px 10px' }}>
                        <span style={{ color: '#6366f1', fontSize: '13px' }}>✅</span>
                        <span style={{ color: '#4338ca', fontSize: '13px', fontWeight: '600', flex: 1 }}>{file.name}</span>
                        <button onClick={() => { setFile(null); setForm1040Error(null); }} style={{
                          background: '#fee2e2', border: 'none', borderRadius: '4px',
                          padding: '2px 8px', fontSize: '11px', color: '#991b1b', cursor: 'pointer', flexShrink: 0
                        }}>Remove</button>
                      </div>
                    ) : (
                      <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>
                        Export from TurboTax, H&R Block, TaxAct, or your accountant
                      </p>
                    )}
                    {form1040Error && (
                      <div style={{
                        marginTop: '8px', background: '#fffbeb',
                        border: '1px solid #f59e0b', borderLeft: '4px solid #f59e0b',
                        borderRadius: '8px', padding: '10px 12px',
                        display: 'flex', gap: '8px', alignItems: 'flex-start'
                      }}>
                        <span style={{ fontSize: '14px', flexShrink: 0 }}>⚠️</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '700', color: '#92400e', fontSize: '12px', marginBottom: '2px' }}>Tax Return Issue</div>
                          <div style={{ color: '#92400e', fontSize: '12px', lineHeight: '1.5', whiteSpace: 'pre-line' }}>{form1040Error}</div>
                          {file && (
                            <button onClick={() => { setFile(null); setForm1040Error(null); }} style={{
                              marginTop: '6px', background: '#fee2e2', border: 'none', borderRadius: '4px',
                              padding: '3px 10px', fontSize: '11px', color: '#991b1b', cursor: 'pointer', fontWeight: '600'
                            }}>Clear uploaded file</button>
                          )}
                        </div>
                        <button onClick={() => setForm1040Error(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: '16px', padding: '0', flexShrink: 0 }}>×</button>
                      </div>
                    )}
                  </div>

                  {/* W-2s */}
                  <div style={{
                    border: w2Files.length > 0 ? '2px solid #22c55e' : '2px dashed #cbd5e1',
                    borderRadius: '14px', padding: '18px',
                    background: w2Files.length > 0 ? '#f0fdf4' : '#f8fafc',
                    transition: 'all 0.2s'
                  }}>
                    <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '14px', marginBottom: '4px' }}>
                      📄 W-2 / Wage Statements <span style={{ color: '#6b7280', fontSize: '12px', fontWeight: '400' }}>Optional — For More Precise Recommendations</span>
                    </div>
                    <p style={{ color: '#6b7280', fontSize: '12px', margin: '0 0 8px' }}>
                      For exact 401(k) and HSA contribution amounts · PDF only · 6MB total across all documents
                    </p>
                    <input
                      type="file" accept=".pdf" multiple onChange={handleW2Change}
                      style={{ display: 'block', width: '100%', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}
                    />
                    {w2Files.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {w2Files.map((w2file, index) => (
                          <div key={index} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: '#dcfce7', borderRadius: '8px', padding: '5px 10px'
                          }}>
                            <span style={{ color: '#16a34a', fontSize: '12px', fontWeight: '600' }}>✓ {w2file.name}</span>
                            <button onClick={() => removeW2(index)} style={{
                              background: '#fee2e2', border: 'none', borderRadius: '4px',
                              padding: '2px 8px', fontSize: '11px', color: '#991b1b', cursor: 'pointer'
                            }}>Remove</button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>
                        Download from your payroll portal (ADP, Workday, Paychex)
                      </p>
                    )}
                    {w2Error && (
                      <div style={{
                        marginTop: '8px', background: '#fffbeb',
                        border: '1px solid #f59e0b', borderLeft: '4px solid #f59e0b',
                        borderRadius: '8px', padding: '10px 12px',
                        display: 'flex', gap: '8px', alignItems: 'flex-start'
                      }}>
                        <span style={{ fontSize: '14px', flexShrink: 0 }}>⚠️</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '700', color: '#92400e', fontSize: '12px', marginBottom: '2px' }}>W-2 Upload Issue</div>
                          <div style={{ color: '#92400e', fontSize: '12px', lineHeight: '1.5' }}>{w2Error}</div>
                          {w2Files.length > 0 && (
                            <button onClick={() => { setW2Files([]); setW2Error(null); }} style={{
                              marginTop: '6px', background: '#fee2e2', border: 'none', borderRadius: '4px',
                              padding: '3px 10px', fontSize: '11px', color: '#991b1b', cursor: 'pointer', fontWeight: '600'
                            }}>Clear uploaded files</button>
                          )}
                        </div>
                        <button onClick={() => setW2Error(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: '16px', padding: '0', flexShrink: 0 }}>×</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT — Helpful guide + status + analyze button */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                  {/* Where to find docs */}
                  <div style={{
                    background: '#f0f9ff', border: '1px solid #bae6fd',
                    borderRadius: '14px', padding: '18px'
                  }}>
                    <div style={{ fontWeight: '700', color: '#0c4a6e', fontSize: '14px', marginBottom: '12px' }}>
                      📋 Where to find your documents
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Tax Return (Form 1040 + All Schedules)</div>
                      {[
                        ['TurboTax', 'My Returns → Download PDF (select "Complete Return")'],
                        ['H&R Block', 'My Account → Tax History → Download Complete Return'],
                        ['TaxAct', 'My Returns → Print/Export → Include All Schedules'],
                        ['Accountant', 'Ask for your complete tax return PDF including all schedules'],
                      ].map(([source, instruction]) => (
                        <div key={source} style={{ display: 'flex', gap: '8px', marginBottom: '5px', alignItems: 'flex-start' }}>
                          <span style={{ color: '#0369a1', fontSize: '12px', fontWeight: '700', minWidth: '80px', flexShrink: 0 }}>{source}</span>
                          <span style={{ color: '#374151', fontSize: '12px' }}>{instruction}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ borderTop: '1px solid #bae6fd', paddingTop: '10px' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>W-2s</div>
                      <div style={{ color: '#374151', fontSize: '12px', lineHeight: '1.6' }}>
                        Download from your payroll portal — ADP, Workday, Paychex, or Gusto. Usually available under <em>Tax Documents</em> or <em>Year-End Forms</em>.
                      </div>
                    </div>
                  </div>

                  {/* Upload status + Analyze button */}
                  <div style={{
                    background: '#fafafa', border: '1px solid #e5e7eb',
                    borderRadius: '14px', padding: '16px'
                  }}>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                        <span style={{ color: '#6b7280' }}>Tax Return</span>
                        <span style={{ color: file ? '#16a34a' : '#ef4444', fontWeight: '600' }}>
                          {file ? '✅ Ready' : '❌ Required'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: '#6b7280' }}>W-2s</span>
                        <span style={{ color: w2Files.length > 0 ? '#16a34a' : '#9ca3af', fontWeight: '600' }}>
                          {w2Files.length > 0 ? `✅ ${w2Files.length} uploaded` : '○ Optional'}
                        </span>
                      </div>
                    </div>

                    {/* Terms & Privacy checkbox */}
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: '10px',
                      background: termsAccepted ? '#f0fdf4' : '#fafafa',
                      border: `1px solid ${termsAccepted ? '#86efac' : '#e5e7eb'}`,
                      borderRadius: '10px', padding: '12px 14px', marginBottom: '12px',
                      transition: 'all 0.2s', cursor: 'pointer'
                    }} onClick={() => setTermsAccepted(!termsAccepted)}>
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '16px', height: '16px', marginTop: '2px', cursor: 'pointer', flexShrink: 0, accentColor: '#6366f1' }}
                      />
                      <span style={{ fontSize: '12px', color: '#4b5563', lineHeight: '1.6' }}>
                        I agree to the{' '}
                        <a href="/terms.html" target="_blank" rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#6366f1', fontWeight: '600', textDecoration: 'underline' }}>
                          Terms of Service
                        </a>
                        {' '}and{' '}
                        <a href="/privacy.html" target="_blank" rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#6366f1', fontWeight: '600', textDecoration: 'underline' }}>
                          Privacy Policy
                        </a>
                        . I understand TaxSavvy.ai provides educational tax analysis, not professional tax advice.
                      </span>
                    </div>

                    <button
                      onClick={handleAnalyze}
                      disabled={!file || loading || !termsAccepted}
                      style={{
                        width: '100%', padding: '14px',
                        fontSize: '15px', fontWeight: '700', color: 'white',
                        background: file && termsAccepted
                          ? 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)'
                          : '#cbd5e1',
                        border: 'none', borderRadius: '10px',
                        cursor: file && termsAccepted ? 'pointer' : 'not-allowed',
                        boxShadow: file && termsAccepted ? '0 8px 24px rgba(99,102,241,0.35)' : 'none',
                        transition: 'all 0.2s'
                      }}
                    >
                      {loading ? '⏳ Analyzing...' : file && termsAccepted
                        ? `🔍 Analyze My Tax Return${w2Files.length > 0 ? ` + ${w2Files.length} W-2${w2Files.length > 1 ? 's' : ''}` : ''}`
                        : !file ? 'Upload Tax Return to Continue'
                        : 'Please Accept Terms to Continue'}
                    </button>
                    <p style={{ color: '#9ca3af', fontSize: '11px', textAlign: 'center', margin: '8px 0 0' }}>
                      🔒 Analyzed in real time · Never stored · PII stripped
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 'extracting' && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              {/* Branded spinner */}
              <div style={{ position: 'relative', width: '90px', height: '90px', margin: '0 auto 32px' }}>
                <div style={{
                  width: '90px', height: '90px',
                  border: '5px solid #e0e7ff',
                  borderTop: '5px solid #6366f1',
                  borderRight: '5px solid #8b5cf6',
                  borderRadius: '50%',
                  animation: 'spin 0.9s linear infinite',
                  position: 'absolute', top: 0, left: 0
                }}/>
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                  fontSize: '28px'
                }}>📄</div>
              </div>
              <h2 style={{ color: '#1e1b4b', marginBottom: '10px', fontWeight: '800', letterSpacing: '-0.5px' }}>
                Reading Your Tax Return...
              </h2>
              <p style={{ color: '#6b7280', fontSize: '15px', maxWidth: '380px', margin: '0 auto 24px', lineHeight: '1.6' }}>
                Extracting all form data, schedules, and line items from your return
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                {['🔒 PII being stripped', '🧠 AI reading forms', '✅ Never stored'].map(t => (
                  <div key={t} style={{
                    background: '#f5f3ff', border: '1px solid #ddd6fe',
                    borderRadius: '100px', padding: '6px 16px',
                    fontSize: '13px', color: '#6366f1', fontWeight: '600'
                  }}>{t}</div>
                ))}
              </div>
              {/* Cold start warning — shows after 8 seconds */}
              {warmingUp && (
                <div style={{
                  marginTop: '28px', background: '#fffbeb',
                  border: '1px solid #f59e0b', borderRadius: '12px',
                  padding: '14px 20px', maxWidth: '380px', margin: '28px auto 0',
                  display: 'flex', gap: '10px', alignItems: 'flex-start'
                }}>
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>⏳</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '700', color: '#92400e', fontSize: '13px', marginBottom: '3px' }}>
                      Almost ready...
                    </div>
                    <div style={{ color: '#92400e', fontSize: '12px', lineHeight: '1.5' }}>
                      Our server is warming up after a period of inactivity. This first request may take up to 60 seconds. Subsequent analyses will be instant.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── AGE INPUT STEP ── */}
          {step === 'age-input' && extractedData && (
            <>
              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ color: '#1f2937', marginBottom: '8px' }}>
                  👤 Confirm Taxpayer Ages
                </h2>
                <p style={{ color: '#6b7280', fontSize: '15px', margin: 0, lineHeight: '1.6' }}>
                  Form 1040 does not include date of birth. Please enter birth year(s) so we
                  can accurately calculate catch-up contribution eligibility (age 50+ for
                  401k/IRA, age 55+ for HSA). Age is determined as of December 31,{' '}
                  {extractedData.tax_year || 2024} per IRS rules.
                </p>
              </div>

              {/* Extracted summary for context */}
              <div style={{
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '28px',
                display: 'flex',
                gap: '24px',
                flexWrap: 'wrap'
              }}>
                <div>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Filing Status</span>
                  <div style={{ fontWeight: '700', color: '#1f2937' }}>
                    {extractedData.taxpayer_profile?.filing_status || '—'}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Tax Year</span>
                  <div style={{ fontWeight: '700', color: '#1f2937' }}>
                    {extractedData.tax_year || '—'}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>AGI</span>
                  <div style={{ fontWeight: '700', color: '#1f2937' }}>
                    ${(extractedData.taxpayer_profile?.agi || 0).toLocaleString()}
                  </div>
                </div>
                {extractedData.taxpayer_profile?.taxpayer_age_2025 === 66 && (
                  <div style={{
                    background: '#dbeafe',
                    border: '1px solid #93c5fd',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontSize: '13px',
                    color: '#1d4ed8',
                    alignSelf: 'center'
                  }}>
                    ✓ 1040 Age/Blindness box checked — taxpayer confirmed 65+
                  </div>
                )}
              </div>

              {/* Birth year inputs */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: extractedData.taxpayer_profile?.filing_status?.toLowerCase().includes('joint')
                  ? '1fr 1fr' : '1fr',
                gap: '24px',
                marginBottom: '24px'
              }}>
                {/* Taxpayer */}
                <div style={{
                  background: '#f8fafc',
                  border: '2px solid #cbd5e1',
                  borderRadius: '12px',
                  padding: '24px'
                }}>
                  <label style={{
                    display: 'block',
                    fontWeight: '700',
                    color: '#1f2937',
                    marginBottom: '6px',
                    fontSize: '16px'
                  }}>
                    Taxpayer Birth Year
                  </label>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
                    {extractedData.taxpayer_profile?.taxpayer_name || 'Primary taxpayer'}
                  </p>
                  <input
                    type="number"
                    min="1900"
                    max={(extractedData.tax_year || 2024) - 18}
                    placeholder="e.g. 1979"
                    value={taxpayerBirthYear}
                    onChange={(e) => setTaxpayerBirthYear(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '20px',
                      fontWeight: '700',
                      border: '2px solid #667eea',
                      borderRadius: '8px',
                      color: '#1f2937',
                      boxSizing: 'border-box',
                      letterSpacing: '2px'
                    }}
                  />
                  {taxpayerBirthYear && (
                    <p style={{ marginTop: '8px', fontSize: '14px', color: '#059669', fontWeight: '600' }}>
                      → Age as of Dec 31, {extractedData.tax_year || 2024}:{' '}
                      <strong>{(extractedData.tax_year || 2024) - parseInt(taxpayerBirthYear)}</strong>
                      {(extractedData.tax_year || 2024) - parseInt(taxpayerBirthYear) >= 50 &&
                        <span style={{ color: '#667eea', marginLeft: '8px' }}>✓ Catch-up eligible (50+)</span>}
                    </p>
                  )}
                </div>

                {/* Spouse — only shown for MFJ */}
                {extractedData.taxpayer_profile?.filing_status?.toLowerCase().includes('joint') && (
                  <div style={{
                    background: '#f8fafc',
                    border: '2px solid #cbd5e1',
                    borderRadius: '12px',
                    padding: '24px'
                  }}>
                    <label style={{
                      display: 'block',
                      fontWeight: '700',
                      color: '#1f2937',
                      marginBottom: '6px',
                      fontSize: '16px'
                    }}>
                      Spouse Birth Year
                    </label>
                    <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
                      {extractedData.taxpayer_profile?.spouse_name || 'Spouse'}
                    </p>
                    <input
                      type="number"
                      min="1900"
                      max={(extractedData.tax_year || 2024) - 18}
                      placeholder="e.g. 1981"
                      value={spouseBirthYear}
                      onChange={(e) => setSpouseBirthYear(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '20px',
                        fontWeight: '700',
                        border: '2px solid #f093fb',
                        borderRadius: '8px',
                        color: '#1f2937',
                        boxSizing: 'border-box',
                        letterSpacing: '2px'
                      }}
                    />
                    {spouseBirthYear && (
                      <p style={{ marginTop: '8px', fontSize: '14px', color: '#059669', fontWeight: '600' }}>
                        → Age as of Dec 31, {extractedData.tax_year || 2024}:{' '}
                        <strong>{(extractedData.tax_year || 2024) - parseInt(spouseBirthYear)}</strong>
                        {(extractedData.tax_year || 2024) - parseInt(spouseBirthYear) >= 50 &&
                          <span style={{ color: '#667eea', marginLeft: '8px' }}>✓ Catch-up eligible (50+)</span>}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Dependents — shown only if dependents exist on return */}
              {(extractedData.taxpayer_profile?.dependents || []).length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: '#1f2937', fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>
                    Dependents
                  </h3>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 14px' }}>
                    Birth years are used to determine Child Tax Credit (under 17), Dependent Care Credit (under 13), and qualifying child status (under 19).
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '16px'
                  }}>
                    {(extractedData.taxpayer_profile.dependents).map((dep, idx) => (
                      <div key={idx} style={{
                        background: '#f8fafc',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        padding: '16px'
                      }}>
                        <label style={{
                          display: 'block',
                          fontWeight: '700',
                          color: '#1f2937',
                          marginBottom: '4px',
                          fontSize: '14px'
                        }}>
                          {dep.name || `Dependent ${idx + 1}`}
                        </label>
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px' }}>
                          {dep.relationship || 'Dependent'}
                        </p>
                        <input
                          type="number"
                          min="1990"
                          max={(extractedData.tax_year || 2024) - 1}
                          placeholder="e.g. 2016"
                          value={dependentBirthYears[idx] || dep.birth_year || ''}
                          onChange={(e) => setDependentBirthYears(prev => ({
                            ...prev,
                            [idx]: e.target.value
                          }))}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '18px',
                            fontWeight: '700',
                            border: '2px solid #94a3b8',
                            borderRadius: '6px',
                            color: '#1f2937',
                            boxSizing: 'border-box'
                          }}
                        />
                        {(dependentBirthYears[idx] || dep.birth_year) && (
                          <p style={{ marginTop: '6px', fontSize: '12px', color: '#059669', fontWeight: '600' }}>
                            Age {(extractedData.tax_year || 2024) - parseInt(dependentBirthYears[idx] || dep.birth_year)}
                            {(extractedData.tax_year || 2024) - parseInt(dependentBirthYears[idx] || dep.birth_year) < 13 && ' · ✓ Dep. Care'}
                            {(extractedData.tax_year || 2024) - parseInt(dependentBirthYears[idx] || dep.birth_year) < 17 && ' · ✓ CTC'}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* IRS note */}
              <div style={{
                background: '#fefce8',
                border: '1px solid #fde047',
                borderRadius: '8px',
                padding: '14px 16px',
                marginBottom: '24px',
                fontSize: '13px',
                color: '#713f12',
                lineHeight: '1.6'
              }}>
                <strong>📋 IRS Age Rules:</strong> Age is determined as of December 31 of the tax year.
                Catch-up contributions: 401(k)/IRA at age 50+, HSA at age 55+.
                The 65+ senior standard deduction is determined separately by the Age/Blindness
                checkbox already on your Form 1040.
              </div>

              {/* Error */}
              {ageInputError && (
                <div style={{
                  background: '#fffbeb',
                  border: '1px solid #f59e0b', borderLeft: '5px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  marginBottom: '20px',
                  color: '#92400e',
                  fontWeight: '600'
                }}>
                  ⚠️ {ageInputError}
                </div>
              )}

              {/* Continue button */}
              <button
                onClick={handleAgeSubmit}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: 'white',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Continue to Analysis →
              </button>
            </>
          )}

          {/* ── SELECTION SCREEN ── */}
          {step === 'selection' && (
            <>
              <h2 style={{ marginBottom: '30px', color: '#1f2937', textAlign: 'center' }}>
                Choose Your Analysis Type
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '30px',
                marginBottom: '30px'
              }}>
                {/* Card 1: Historical Analysis */}
                <div
                  onClick={() => handleModeSelection('historical')}
                  style={{
                    border: '2px solid #cbd5e1',
                    borderRadius: '16px',
                    padding: '40px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    textAlign: 'center'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>📊</div>
                  <h3 style={{ fontSize: '24px', marginBottom: '15px', marginTop: 0 }}>
                    Historical Analysis
                  </h3>
                  <p style={{ fontSize: '16px', lineHeight: '1.6', opacity: 0.9, margin: 0 }}>
                    Review your {extractedData?.tax_year || '2024'} tax return and discover optimization opportunities based on actual filed data.
                  </p>
                  <div style={{
                    marginTop: '30px',
                    padding: '12px 24px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    Click to Analyze {extractedData?.tax_year || '2024'} Return
                  </div>
                </div>

                {/* Card 2: forward planning — year derived from the return */}
                <div
                  onClick={() => handleModeSelection('forward')}
                  style={{
                    border: '2px solid #cbd5e1',
                    borderRadius: '16px',
                    padding: '40px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    textAlign: 'center'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(240, 147, 251, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>📈</div>
                  <h3 style={{ fontSize: '24px', marginBottom: '15px', marginTop: 0 }}>
                    {pyr} Tax Planning
                  </h3>
                  <p style={{ fontSize: '16px', lineHeight: '1.6', opacity: 0.9, margin: 0 }}>
                    Optimize for the upcoming tax year with forward-looking strategies based on your current situation.
                  </p>
                  <div style={{
                    marginTop: '30px',
                    padding: '12px 24px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    Click to Plan for {pyr}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* NEW: PAYSTUB UPLOAD SCREEN (for forward planning) */}
          {/* ── SELF-EMPLOYED INCOME ENTRY SCREEN ── */}
          {step === 'se-income' && (
            <>
              <button
                onClick={() => { setIsSelfEmployed(false); setSelfEmployedIncome({ month: '', ytdNetProfit: '' }); setStep('selection'); }}
                style={{ marginBottom: '30px', padding: '12px 24px', background: 'white',
                  border: '2px solid #667eea', borderRadius: '8px', color: '#667eea',
                  fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
              >← Back to Selection</button>

              <h2 style={{ marginBottom: '8px', color: '#1f2937', textAlign: 'center' }}>💼 {pyr} Self-Employment Income</h2>
              <p style={{ color: '#6b7280', fontSize: '15px', textAlign: 'center', marginBottom: '30px' }}>
                Your return shows self-employment income. Please enter your {pyr} year-to-date net profit.
              </p>

              <div style={{ background: '#f0f9ff', border: '2px solid #0284c7', borderRadius: '16px',
                padding: '32px', maxWidth: '560px', margin: '0 auto 30px' }}>
                <h3 style={{ margin: '0 0 24px', color: '#0c4a6e', fontSize: '18px' }}>📅 Income Through End of Month</h3>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                    Income as of end of which month? *
                  </label>
                  <select
                    value={selfEmployedIncome.month}
                    onChange={(e) => setSelfEmployedIncome(prev => ({ ...prev, month: e.target.value }))}
                    style={{ width: '100%', padding: '12px 16px', fontSize: '16px', fontWeight: '600',
                      border: '2px solid #cbd5e1', borderRadius: '8px', background: 'white', cursor: 'pointer' }}
                  >
                    <option value="">— Select month —</option>
                    {['January','February','March','April','May','June',
                      'July','August','September','October','November','December'].map((m, i) => (
                      <option key={i} value={i + 1}>{m} {pyr}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                    Year-to-Date Net Profit (after all business expenses) *
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', border: '2px solid #cbd5e1',
                    borderRadius: '8px', background: 'white', padding: '4px 12px' }}>
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#6b7280', marginRight: '8px' }}>$</span>
                    <input
                      key="se-ytd-input"
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g. 85000"
                      defaultValue=""
                      style={{ flex: 1, border: 'none', outline: 'none', fontSize: '20px',
                        fontWeight: '700', color: '#059669', background: 'transparent', padding: '8px 0' }}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value.replace(/,/g, ''));
                        if (!isNaN(val)) setSelfEmployedIncome(prev => ({ ...prev, ytdNetProfit: val }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const val = parseFloat(e.target.value.replace(/,/g, ''));
                          if (!isNaN(val)) setSelfEmployedIncome(prev => ({ ...prev, ytdNetProfit: val }));
                        }
                      }}
                    />
                  </div>
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: '6px 0 0' }}>
                    Press Enter or click away to confirm. Net profit = revenue minus all expenses.
                  </p>
                </div>

                {selfEmployedIncome.month && selfEmployedIncome.ytdNetProfit && (
                  <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '10px',
                    padding: '16px', marginBottom: '24px' }}>
                    <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '4px', fontWeight: '600' }}>
                      📊 Projected {pyr} Annual Net Profit
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#047857' }}>
                      ${Math.round(parseFloat(String(selfEmployedIncome.ytdNetProfit).replace(/,/g,'')) / parseInt(selfEmployedIncome.month) * 12).toLocaleString()}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      ${Number(selfEmployedIncome.ytdNetProfit).toLocaleString()} ÷ {selfEmployedIncome.month} months × 12
                    </div>
                  </div>
                )}

                <button
                  disabled={!selfEmployedIncome.month || !selfEmployedIncome.ytdNetProfit || loading}
                  onClick={handleSelfEmployedAnalysis}
                  style={{ width: '100%', padding: '16px', fontSize: '18px', fontWeight: 'bold',
                    color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer',
                    background: (!selfEmployedIncome.month || !selfEmployedIncome.ytdNetProfit)
                      ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                >
                  {loading ? 'Analyzing...' : `✅ Run ${yr} Tax Analysis`}
                </button>
              </div>
            </>
          )}

          {step === 'paystub-upload' && (
            <>
              {/* Back button */}
              <button
                onClick={() => setStep('selection')}
                style={{
                  marginBottom: '24px', padding: '10px 20px', background: 'white',
                  border: '2px solid #667eea', borderRadius: '8px', color: '#667eea',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                }}
              >← Back to Selection</button>

              {/* Header */}
              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ color: '#1f2937', marginBottom: '6px', fontSize: '22px' }}>
                  {pyr} Forward Tax Planning
                </h2>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                  Upload your most recent paystub for precise projections, or continue without one for general planning.
                </p>
              </div>

              {/* 2-column layout — everything visible without scrolling */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' }}>

                {/* LEFT — Upload zone */}
                <div style={{
                  border: '2px dashed #c7d2fe', borderRadius: '16px',
                  padding: '28px', textAlign: 'center', background: '#fafbff',
                  transition: 'border-color 0.2s'
                }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>📄</div>
                  <p style={{ fontWeight: '700', color: '#1f2937', fontSize: '15px', marginBottom: '6px' }}>
                    YTD Paystub (Optional)
                  </p>
                  <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px', lineHeight: '1.5' }}>
                    PDF only · Max 20MB · Most recent paystub preferred
                  </p>
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    onChange={handlePaystubChange}
                    style={{ display: 'block', margin: '0 auto 12px', fontSize: '14px', cursor: 'pointer' }}
                  />
                  <p style={{ color: '#6366f1', fontSize: '12px', margin: 0 }}>
                    💡 Download from ADP, Workday, Paychex or your payroll portal
                  </p>

                  {paystubError && (
                    <div style={{
                      marginTop: '10px', background: '#fffbeb',
                      border: '1px solid #f59e0b', borderLeft: '4px solid #f59e0b',
                      borderRadius: '8px', padding: '10px 12px',
                      display: 'flex', gap: '8px', alignItems: 'flex-start', textAlign: 'left'
                    }}>
                      <span style={{ fontSize: '14px', flexShrink: 0 }}>⚠️</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', color: '#92400e', fontSize: '12px', marginBottom: '2px' }}>Paystub Upload Issue</div>
                        <div style={{ color: '#92400e', fontSize: '12px', lineHeight: '1.5' }}>{paystubError}</div>
                      </div>
                      <button onClick={() => setPaystubError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: '16px', padding: '0', flexShrink: 0 }}>×</button>
                    </div>
                  )}
                  {paystubFiles.length > 0 && (
                    <div style={{ marginTop: '16px', textAlign: 'left' }}>
                      <p style={{ fontWeight: '700', color: '#1f2937', fontSize: '13px', marginBottom: '8px' }}>
                        ✅ {paystubFiles.length} file{paystubFiles.length > 1 ? 's' : ''} selected:
                      </p>
                      {paystubFiles.map((stub, index) => (
                        <div key={index} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '8px 12px', marginBottom: '6px',
                          background: index === 0 ? '#dcfce7' : '#f9fafb',
                          border: index === 0 ? '1.5px solid #16a34a' : '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}>
                          <div>
                            <span style={{ fontSize: '13px', fontWeight: index === 0 ? '700' : '400', color: index === 0 ? '#16a34a' : '#374151' }}>
                              {index === 0 ? '🟢 ' : '✓ '}{stub.name}
                            </span>
                            {index === 0 && (
                              <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '2px' }}>Will be used for analysis</div>
                            )}
                          </div>
                          <button
                            onClick={() => removePaystub(index)}
                            style={{
                              background: '#fee2e2', border: 'none', borderRadius: '6px',
                              padding: '4px 10px', fontSize: '12px', color: '#991b1b',
                              cursor: 'pointer', fontWeight: '600', flexShrink: 0
                            }}
                          >Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* RIGHT — Why + action */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  {/* Why upload box */}
                  <div style={{
                    background: '#f0f9ff', border: '1px solid #bae6fd',
                    borderRadius: '14px', padding: '20px'
                  }}>
                    <h3 style={{ color: '#0c4a6e', fontSize: '14px', fontWeight: '700', marginBottom: '12px', marginTop: 0 }}>
                      📊 Why upload a paystub?
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        ['With paystub', 'Precise year-end income projection', '#dcfce7', '#16a34a'],
                        ['With paystub', 'Actual YTD withholding analysis', '#dcfce7', '#16a34a'],
                        ['With paystub', 'Real-time 401(k) & HSA tracking', '#dcfce7', '#16a34a'],
                        ['Without paystub', `${yr} data used to estimate ${pyr}`, '#fef3c7', '#92400e'],
                      ].map(([label, text, bg, color]) => (
                        <div key={text} style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          background: bg, borderRadius: '8px', padding: '8px 12px'
                        }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color, flexShrink: 0 }}>{label}</span>
                          <span style={{ fontSize: '13px', color: '#374151' }}>{text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <button
                    onClick={handleForwardPlanningAnalysis}
                    disabled={loading}
                    style={{
                      width: '100%', padding: '16px', fontSize: '16px', fontWeight: '700',
                      color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer',
                      background: paystubFiles.length > 0
                        ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                        : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                      boxShadow: paystubFiles.length > 0 ? '0 8px 24px rgba(99,102,241,0.35)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    {loading ? '⏳ Processing...' :
                      paystubFiles.length > 0
                        ? `✅ Analyze with Paystub (Precise)`
                        : '→ Continue Without Paystub (General)'}
                  </button>

                  <p style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center', margin: 0 }}>
                    {paystubFiles.length > 0
                      ? `Using your most recent paystub for precise ${pyr} projections`
                      : 'General planning based on your 2024 tax return data'}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* NEW: PAYSTUB CONFIRMATION SCREEN */}
          {step === 'paystub-confirm' && editablePaystubData && (
            <>
              <button
                onClick={() => setStep('paystub-upload')}
                style={{
                  marginBottom: '20px', padding: '12px 24px', background: 'white',
                  border: '2px solid #667eea', borderRadius: '8px', color: '#667eea',
                  fontSize: '16px', fontWeight: '600', cursor: 'pointer'
                }}
              >
                ← Back to Upload
              </button>

              {/* Progress indicator */}
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h2 style={{ margin: '0 0 12px', color: '#1f2937' }}>
                  📋 Review Paystub {currentPaystubIndex + 1} of {allExtractedPaystubs.length}
                </h2>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  {allExtractedPaystubs.map((_, i) => (
                    <div key={i} style={{
                      width: '34px', height: '34px', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 'bold', fontSize: '14px',
                      background: i < currentPaystubIndex ? '#10b981' : i === currentPaystubIndex ? '#667eea' : '#e5e7eb',
                      color: i <= currentPaystubIndex ? 'white' : '#6b7280'
                    }}>
                      {i < currentPaystubIndex ? '✓' : i + 1}
                    </div>
                  ))}
                </div>
              </div>

              {/* Attribution selector */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>Belongs to:</span>
                <select
                  value={editablePaystubData._owner || 'taxpayer'}
                  onChange={(e) => setEditablePaystubData({ ...editablePaystubData, _owner: e.target.value })}
                  style={{
                    padding: '8px 16px', borderRadius: '8px', border: '2px solid #667eea',
                    fontWeight: '700', fontSize: '14px', color: '#4338ca', cursor: 'pointer', background: '#eef2ff'
                  }}
                >
                  <option value="taxpayer">
                    {(extractedDataRef.current || extractedData)?.taxpayer_profile?.taxpayer_name || 'Primary Taxpayer'}
                  </option>
                  {(extractedDataRef.current || extractedData)?.taxpayer_profile?.filing_status?.toLowerCase().includes('joint') && (
                    <option value="spouse">
                      {(extractedDataRef.current || extractedData)?.taxpayer_profile?.spouse_name || 'Spouse'}
                    </option>
                  )}
                </select>
                {editablePaystubData.employer_name && (
                  <span style={{ fontSize: '13px', color: '#9ca3af' }}>— {editablePaystubData.employer_name}</span>
                )}
              </div>

              {/* Duplicate warning */}
              {paystubDuplicateWarning && (
                <div style={{
                  background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '10px',
                  padding: '16px', marginBottom: '16px', color: '#92400e', fontSize: '14px'
                }}>
                  <strong>⚠️ Duplicate Detected:</strong> {paystubDuplicateWarning}
                </div>
              )}

              <div style={{
                background: '#f0f9ff', border: '2px solid #0284c7',
                borderRadius: '12px', padding: '24px', marginBottom: '30px'
              }}>
                <h3 style={{ marginTop: 0, color: '#0c4a6e', fontSize: '18px', marginBottom: '20px' }}>
                  📊 Paystub Information (Editable)
                  {editablePaystubData.employer_name && (
                    <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '10px', color: '#0369a1' }}>
                      — {editablePaystubData.employer_name}
                    </span>
                  )}
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '20px',
                  marginBottom: '20px'
                }}>
                  {/* Pay Period End - EDITABLE */}
                  <div style={{ background: 'white', padding: '16px', borderRadius: '8px' }}>
                    <label style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                      Pay Period End
                    </label>
                    <input
                      type="date"
                      value={editablePaystubData.pay_period_end || ''}
                      onChange={(e) => setEditablePaystubData({
                        ...editablePaystubData,
                        pay_period_end: e.target.value
                      })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '16px',
                        border: '2px solid #cbd5e1',
                        borderRadius: '6px',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}
                    />
                  </div>

                  {/* Pay Frequency - DROPDOWN */}
                  <div style={{ background: 'white', padding: '16px', borderRadius: '8px' }}>
                    <label style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                      Pay Frequency
                    </label>
                    <select
                      value={editablePaystubData.pay_frequency || 'biweekly'}
                      onChange={(e) => setEditablePaystubData({
                        ...editablePaystubData,
                        pay_frequency: e.target.value
                      })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '16px',
                        border: '2px solid #cbd5e1',
                        borderRadius: '6px',
                        fontWeight: '600',
                        color: '#0891b2',
                        textTransform: 'capitalize',
                        cursor: 'pointer',
                        background: 'white'
                      }}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Biweekly (Every 2 weeks)</option>
                      <option value="semimonthly">Semimonthly (15th & 30th)</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  
                  {/* YTD Gross - EDITABLE */}
                  <div style={{ background: 'white', padding: '16px', borderRadius: '8px' }}>
                    <label style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                      YTD Gross Pay
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#6b7280', marginRight: '4px' }}>$</span>
                      <input
                        type="number"
                        value={editablePaystubData.ytd_gross || ''}
                        onChange={(e) => setEditablePaystubData({
                          ...editablePaystubData,
                          ytd_gross: parseFloat(e.target.value) || 0
                        })}
                        style={{
                          flex: 1,
                          padding: '10px',
                          fontSize: '18px',
                          border: '2px solid #cbd5e1',
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          color: '#059669'
                        }}
                      />
                    </div>
                  </div>

                  {/* YTD Federal Withholding - EDITABLE */}
                  <div style={{ background: 'white', padding: '16px', borderRadius: '8px' }}>
                    <label style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                      YTD Federal Withholding
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#6b7280', marginRight: '4px' }}>$</span>
                      <input
                        type="number"
                        value={editablePaystubData.ytd_federal_withholding || ''}
                        onChange={(e) => setEditablePaystubData({
                          ...editablePaystubData,
                          ytd_federal_withholding: parseFloat(e.target.value) || 0
                        })}
                        style={{
                          flex: 1,
                          padding: '10px',
                          fontSize: '18px',
                          border: '2px solid #cbd5e1',
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          color: '#dc2626'
                        }}
                      />
                    </div>
                  </div>

                  {/* YTD 401k - EDITABLE */}
                  <div style={{ background: 'white', padding: '16px', borderRadius: '8px' }}>
                    <label style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                      YTD 401(k) Contributions
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#6b7280', marginRight: '4px' }}>$</span>
                      <input
                        type="number"
                        value={editablePaystubData.ytd_401k || 0}
                        onChange={(e) => setEditablePaystubData({
                          ...editablePaystubData,
                          ytd_401k: parseFloat(e.target.value) || 0
                        })}
                        style={{
                          flex: 1,
                          padding: '10px',
                          fontSize: '18px',
                          border: '2px solid #cbd5e1',
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          color: '#7c3aed'
                        }}
                      />
                    </div>
                  </div>

                  {/* YTD HSA - EDITABLE */}
                  <div style={{ background: 'white', padding: '16px', borderRadius: '8px' }}>
                    <label style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                      YTD HSA Contributions
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#6b7280', marginRight: '4px' }}>$</span>
                      <input
                        type="number"
                        value={editablePaystubData.ytd_hsa || 0}
                        onChange={(e) => setEditablePaystubData({
                          ...editablePaystubData,
                          ytd_hsa: parseFloat(e.target.value) || 0
                        })}
                        style={{
                          flex: 1,
                          padding: '10px',
                          fontSize: '18px',
                          border: '2px solid #cbd5e1',
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          color: '#ea580c'
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div style={{
                  background: '#fffbeb',
                  border: '1px solid #fde047',
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '20px'
                }}>
                  <p style={{ margin: 0, color: '#78350f', fontSize: '14px', lineHeight: '1.6' }}>
                    <strong>✏️ Review & Edit:</strong> Please verify all extracted data matches your paystub. 
                    You can edit any field if the extraction was incorrect. Pay special attention to the <strong>Pay Frequency</strong> setting 
                    (Biweekly = every 2 weeks, Semimonthly = 15th & last day of month).
                  </p>
                </div>
              </div>

              {error && (
                <div style={{
                  background: '#fffbeb',
                  border: '1px solid #f59e0b', borderLeft: '5px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                  color: '#92400e'
                }}>
                  {error}
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={handleConfirmPaystub}
                  disabled={loading}
                  style={{
                    padding: '16px 48px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: 'white',
                    background: currentPaystubIndex + 1 < allExtractedPaystubs.length
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    transition: 'all 0.3s'
                  }}
                  onMouseOver={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                  onMouseOut={(e) => !loading && (e.target.style.transform = 'translateY(0)')}
                >
                  {loading
                    ? (currentPaystubIndex + 1 < allExtractedPaystubs.length ? 'Processing...' : 'Analyzing...')
                    : currentPaystubIndex + 1 < allExtractedPaystubs.length
                      ? `✅ Confirm & Review Next Paystub (${currentPaystubIndex + 2} of ${allExtractedPaystubs.length})`
                      : '✅ Confirm All Paystubs →'}
                </button>
              </div>
            </>
          )}

          {step === 'non-w2-confirm' && nonW2Items.length > 0 && (
            <>
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h2 style={{ margin: '0 0 8px', color: '#1f2937' }}>
                  📄 Confirm Non-W2 Income for {((extractedDataRef.current || extractedData)?.tax_year || 2024) + 1}
                </h2>
                <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>
                  We found the following non-W2 income on your {(extractedDataRef.current || extractedData)?.tax_year || 2024} return.
                  Will these continue? Update amounts if they've changed.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '30px' }}>
                {nonW2Items.map((item) => (
                  <div key={item.key} style={{
                    background: 'white', border: '2px solid #e5e7eb',
                    borderRadius: '12px', padding: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', fontSize: '16px', color: '#1f2937', marginBottom: '4px' }}>
                          {item.label}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>{item.description}</div>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                          {(extractedDataRef.current || extractedData)?.tax_year || 2024} amount:{' '}
                          <strong style={{ color: item.amount2024 < 0 ? '#dc2626' : '#059669' }}>
                            {item.amount2024 < 0 ? '-' : ''}${Math.abs(item.amount2024).toLocaleString()}
                          </strong>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button
                          onClick={() => setConfirmedNonW2(prev => ({ ...prev, [item.key]: item.amount2024 }))}
                          style={{
                            padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '13px',
                            border: '2px solid',
                            borderColor: confirmedNonW2[item.key] === item.amount2024 ? '#10b981' : '#d1d5db',
                            background: confirmedNonW2[item.key] === item.amount2024 ? '#ecfdf5' : 'white',
                            color: confirmedNonW2[item.key] === item.amount2024 ? '#065f46' : '#6b7280',
                            cursor: 'pointer'
                          }}
                        >✅ Same amount</button>
                        <button
                          onClick={() => setConfirmedNonW2(prev => ({ ...prev, [item.key]: '' }))}
                          style={{
                            padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '13px',
                            border: '2px solid',
                            borderColor: confirmedNonW2[item.key] === '' ? '#f59e0b' : '#d1d5db',
                            background: confirmedNonW2[item.key] === '' ? '#fffbeb' : 'white',
                            color: confirmedNonW2[item.key] === '' ? '#92400e' : '#6b7280',
                            cursor: 'pointer'
                          }}
                        >✏️ Different amount</button>
                        <button
                          onClick={() => setConfirmedNonW2(prev => ({ ...prev, [item.key]: 0 }))}
                          style={{
                            padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '13px',
                            border: '2px solid',
                            borderColor: confirmedNonW2[item.key] === 0 ? '#ef4444' : '#d1d5db',
                            background: confirmedNonW2[item.key] === 0 ? '#fef2f2' : 'white',
                            color: confirmedNonW2[item.key] === 0 ? '#991b1b' : '#6b7280',
                            cursor: 'pointer'
                          }}
                        >❌ Won't continue</button>
                      </div>
                    </div>
                    {confirmedNonW2[item.key] === '' && (
                      <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>
                          Expected {((extractedDataRef.current || extractedData)?.tax_year || 2024) + 1} net {item.type === 'rental' ? 'income/loss' : 'profit'}:
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', background: '#f9fafb', border: '2px solid #f59e0b', borderRadius: '8px', padding: '4px 12px' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', marginRight: '4px' }}>$</span>
                          <input
                            key={`nonw2-${item.key}`}
                            type="text"
                            inputMode="decimal"
                            placeholder="e.g. 33000 or -4000"
                            defaultValue=""
                            style={{
                              border: 'none', background: 'transparent', fontSize: '16px',
                              fontWeight: '600', color: '#1f2937', outline: 'none', width: '220px'
                            }}
                            onBlur={(e) => {
                              const val = parseFloat(e.target.value.replace(/,/g, ''));
                              if (!isNaN(val)) setConfirmedNonW2(prev => ({ ...prev, [item.key]: val }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const val = parseFloat(e.target.value.replace(/,/g, ''));
                                if (!isNaN(val)) setConfirmedNonW2(prev => ({ ...prev, [item.key]: val }));
                              }
                            }}
                          />
                        </div>
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>Use negative for a loss</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ textAlign: 'center' }}>
                <button
                  disabled={loading || nonW2Items.some(item => confirmedNonW2[item.key] === '')}
                  onClick={async () => {
                    const activeData = extractedDataRef.current || extractedData;
                    const isMFJ = activeData?.taxpayer_profile?.filing_status?.toLowerCase().includes('joint');
                    await runPreciseAnalysis(confirmedPaystubs, confirmedNonW2, isMFJ, activeData);
                  }}
                  style={{
                    padding: '16px 48px', fontSize: '18px', fontWeight: 'bold', color: 'white',
                    background: nonW2Items.some(item => confirmedNonW2[item.key] === '')
                      ? '#9ca3af'
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none', borderRadius: '12px', cursor: 'pointer'
                  }}
                >
                  {loading ? 'Analyzing...' : '✅ Confirm & Run Analysis'}
                </button>
                {nonW2Items.some(item => confirmedNonW2[item.key] === '') && (
                  <p style={{ color: '#f59e0b', fontSize: '14px', marginTop: '8px' }}>
                    ⚠️ Please enter a {((extractedDataRef.current || extractedData)?.tax_year || 2024) + 1} amount for all items marked "Different amount"
                  </p>
                )}
              </div>
            </>
          )}

          {step === 'analyzing' && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ position: 'relative', width: '90px', height: '90px', margin: '0 auto 32px' }}>
                <div style={{
                  width: '90px', height: '90px',
                  border: '5px solid #e0e7ff',
                  borderTop: '5px solid #6366f1',
                  borderRight: '5px solid #8b5cf6',
                  borderRadius: '50%',
                  animation: 'spin 0.9s linear infinite',
                  position: 'absolute', top: 0, left: 0
                }}/>
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                  fontSize: '28px'
                }}>🧠</div>
              </div>
              <h2 style={{ color: '#1e1b4b', marginBottom: '10px', fontWeight: '800', letterSpacing: '-0.5px' }}>
                {loadingMessage.includes('Extracting') ? 'Reading Your Paystub...' : 'Finding Your Tax Savings...'}
              </h2>
              <p style={{ color: '#6b7280', fontSize: '15px', maxWidth: '420px', margin: '0 auto 24px', lineHeight: '1.6' }}>
                {loadingMessage.includes('Extracting')
                  ? 'Extracting your year-to-date earnings and withholdings'
                  : 'Running your return against 60+ CPA-verified tax rules and generating your personalized savings plan'}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                {['📋 60+ Tax Rules', '🏛️ IRS-Compliant', '👨‍💼 CPA-Verified'].map(t => (
                  <div key={t} style={{
                    background: '#f5f3ff', border: '1px solid #ddd6fe',
                    borderRadius: '100px', padding: '6px 16px',
                    fontSize: '13px', color: '#6366f1', fontWeight: '600'
                  }}>{t}</div>
                ))}
              </div>
            </div>
          )}

          {step === 'results' && analysis && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
              padding: '32px',
              marginBottom: '20px'
            }}>
              <>
              {/* ── REPORT HEADER ── */}
              <div style={{
                background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
                borderRadius: '16px', padding: '28px 32px', marginBottom: '24px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: '16px'
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.15)', borderRadius: '8px',
                      padding: '4px 12px', fontSize: '12px', color: 'rgba(255,255,255,0.8)',
                      fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px'
                    }}>
                      {extractedData?.tax_year || '2024'} Tax Year · AI-Powered · IRS-Compliant Rules
                    </div>
                  </div>
                  <h2 style={{ margin: 0, color: '#fff', fontSize: '24px', fontWeight: '800', letterSpacing: '-0.5px' }}>
                    Your Personalized Tax Savings Plan
                  </h2>
                  <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                    Generated by TaxSavvy.ai · {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    onClick={() => window.print()}
                    style={{
                      padding: '10px 20px',
                      background: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '8px', cursor: 'pointer',
                      fontSize: '14px', fontWeight: '600', color: 'white',
                      display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                  >🖨️ Save / Print PDF</button>
                  <button
                    onClick={handleReset}
                    style={{
                      padding: '10px 20px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px', cursor: 'pointer',
                      fontSize: '14px', fontWeight: '500', color: 'rgba(255,255,255,0.8)'
                    }}
                  >← New Analysis</button>
                </div>
              </div>

              {/* ── SUMMARY CARDS ── */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px', marginBottom: '28px'
              }}>
                {/* Total Savings — hero card */}
                <div style={{
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
                  color: 'white', padding: '24px', borderRadius: '14px',
                  boxShadow: '0 8px 24px rgba(5,150,105,0.3)', gridColumn: 'span 1'
                }}>
                  <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
                    💰 Potential Savings
                  </div>
                  <div style={{ fontSize: '38px', fontWeight: '900', letterSpacing: '-1px' }}>
                    ${(analysis.potential_savings || 0).toLocaleString()}
                  </div>
                  {analysis.simple_sum_savings && analysis.simple_sum_savings !== analysis.potential_savings && (
                    <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '6px', lineHeight: '1.4' }}>
                      {analysis.potential_savings < analysis.simple_sum_savings
                        ? `Bracket compression from $${analysis.simple_sum_savings.toLocaleString()}`
                        : `After bracket optimization from $${analysis.simple_sum_savings.toLocaleString()}`}
                    </div>
                  )}
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
                  WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
                  color: 'white', padding: '24px', borderRadius: '14px',
                  boxShadow: '0 8px 24px rgba(99,102,241,0.25)'
                }}>
                  <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
                    📋 Recommendations
                  </div>
                  <div style={{ fontSize: '38px', fontWeight: '900' }}>
                    {analysis.total_recommendations || 0}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.75, marginTop: '6px' }}>
                    Actionable savings opportunities
                  </div>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
                  color: 'white', padding: '24px', borderRadius: '14px',
                  boxShadow: '0 8px 24px rgba(220,38,38,0.25)'
                }}>
                  <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
                    🚨 Critical Actions
                  </div>
                  <div style={{ fontSize: '38px', fontWeight: '900' }}>
                    {analysis.critical_actions || 0}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.75, marginTop: '6px' }}>
                    Require immediate attention
                  </div>
                </div>
              </div>

              {/* ── RECOMMENDATIONS SECTION HEADER ── */}
              <div style={{ marginTop: '8px' }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: '20px',
                  paddingBottom: '16px', borderBottom: '2px solid #e5e7eb'
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px', color: '#1e1b4b', fontSize: '20px', fontWeight: '800' }}>
                      Detailed Recommendations
                    </h3>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '13px' }}>
                      Ranked by potential impact · IRS-compliant rules · Consult your CPA before acting
                    </p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#6b7280' }}>
                    <input
                      type="checkbox"
                      checked={showZeroSavings}
                      onChange={(e) => setShowZeroSavings(e.target.checked)}
                      style={{ cursor: 'pointer', width: '15px', height: '15px' }}
                    />
                    Show $0 items
                  </label>
                </div>

                {analysis.recommendations && analysis.recommendations.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {analysis.recommendations.map((rec, index) => (
                      <div
                        key={index}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderLeft: `5px solid ${getPriorityColor(rec.priority)}`,
                          borderRadius: '12px',
                          padding: '24px',
                          background: '#ffffff',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                          position: 'relative'
                        }}
                      >
                        {/* Top row: number + title + priority + savings */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '16px' }}>
                          {/* Number circle */}
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                            background: getPriorityColor(rec.priority),
                            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '15px', fontWeight: '800'
                          }}>{index + 1}</div>

                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
                              <h4 style={{ margin: 0, color: '#1e1b4b', fontSize: '17px', fontWeight: '700' }}>
                                {rec.title}
                              </h4>
                              <span style={{
                                background: getPriorityColor(rec.priority),
                                color: 'white', padding: '2px 10px', borderRadius: '100px',
                                fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px'
                              }}>{rec.priority}</span>
                            </div>

                            {/* Meta row */}
                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                              <span style={{
                                background: '#f3f4f6', borderRadius: '6px', padding: '3px 10px',
                                fontSize: '12px', color: '#6b7280', fontWeight: '500'
                              }}>{rec.category}</span>
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                Confidence: <span style={{ color: getConfidenceColor(rec.confidence), fontWeight: '600' }}>{rec.confidence}</span>
                              </span>
                              {rec.timeline && <span style={{ fontSize: '12px', color: '#6b7280' }}>⏱ {rec.timeline}</span>}
                              {rec.deadline && <span style={{ fontSize: '12px', color: '#ef4444', fontWeight: '600' }}>⚠️ Due: {rec.deadline}</span>}
                            </div>
                          </div>

                          {/* Savings amount */}
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Savings</div>
                            <div style={{ fontSize: '24px', fontWeight: '900', color: '#059669', letterSpacing: '-0.5px' }}>
                              ${(rec.annual_savings || 0).toLocaleString()}
                            </div>
                          </div>
                        </div>

                        {/* Recommended Action */}
                        <div style={{
                          background: '#f0f9ff', padding: '14px 16px',
                          borderRadius: '10px', marginBottom: '14px',
                          borderLeft: '4px solid #0284c7'
                        }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                            Recommended Action
                          </div>
                          <p style={{ margin: 0, color: '#1f2937', fontSize: '14px', lineHeight: '1.65' }}>
                            {rec.recommended_action}
                          </p>
                        </div>

                        {/* Explanation */}
                        {rec.detailed_explanation && (
                          <div style={{ marginBottom: '14px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                              Explanation
                            </div>
                            <p style={{ margin: 0, color: '#4b5563', fontSize: '14px', lineHeight: '1.65' }}>
                              {rec.detailed_explanation}
                            </p>
                          </div>
                        )}

                        {/* Validation Flags */}
                        {rec.validation_flags && rec.validation_flags.length > 0 && (
                          <div style={{
                            background: '#fffbeb', border: '1px solid #f59e0b',
                            borderLeft: '4px solid #f59e0b', borderRadius: '8px',
                            padding: '12px 14px', marginBottom: '14px'
                          }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#92400e', marginBottom: '6px' }}>⚠️ Validation Warnings</div>
                            <ul style={{ margin: 0, paddingLeft: '18px', color: '#92400e', fontSize: '13px' }}>
                              {rec.validation_flags.map((flag, i) => <li key={i}>{flag}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Footer details */}
                        {(rec.implementation_difficulty || rec.irs_publication) && (
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
                            {rec.implementation_difficulty && (
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                Difficulty: <strong style={{ color: '#374151' }}>{rec.implementation_difficulty}</strong>
                              </span>
                            )}
                            {rec.irs_publication && (
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                IRS Ref: <strong style={{ color: '#374151' }}>{rec.irs_publication}</strong>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center', padding: '48px',
                    background: '#f0fdf4', borderRadius: '12px',
                    border: '1px solid #86efac'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
                    <p style={{ color: '#15803d', fontSize: '17px', fontWeight: '700', margin: '0 0 8px' }}>
                      Your return is well-optimized!
                    </p>
                    <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                      No additional savings opportunities were found for this tax year.
                    </p>
                  </div>
                )}
              </div>

              {/* Combined Actions Impact */}
              {analysis.combined_actions_impact && (
                <div style={{ marginTop: '32px' }}>
                  <div style={{ paddingBottom: '16px', borderBottom: '2px solid #e5e7eb', marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 4px', color: '#1e1b4b', fontSize: '20px', fontWeight: '800' }}>
                      📊 Impact of Implementing All Recommendations
                    </h3>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '13px' }}>
                      See how each action builds on the previous to maximize your total savings
                    </p>
                  </div>
                  
                  {/* Summary Box */}
                  <div style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '24px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    marginBottom: '30px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Total Annual Tax Savings
                    </div>
                    <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '20px' }}>
                      ${(analysis.combined_actions_impact.total_impact?.total_tax_savings || 
                         analysis.combined_actions_impact.total_savings || 
                         analysis.potential_savings || 0).toLocaleString()}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                      <div>
                        <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Actions</div>
                        <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
                          {analysis.combined_actions_impact.sequential_breakdown?.length || 0}
                        </div>
                      </div>
                      {analysis.combined_actions_impact.after_all_actions?.tax_bracket && (
                        <div>
                          <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Tax Bracket</div>
                          <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
                            {analysis.combined_actions_impact.after_all_actions.tax_bracket}%
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Step-by-Step Impact */}
                  {analysis.combined_actions_impact.sequential_breakdown && analysis.combined_actions_impact.sequential_breakdown.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: '28px', fontWeight: 'bold', textAlign: 'center', marginBottom: '10px', color: '#1f2937' }}>
                        Step-by-Step Impact
                      </h4>
                      <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '30px', fontSize: '16px' }}>
                        See how each action builds on the previous ones
                      </p>
                      
                      <div style={{ position: 'relative', paddingLeft: '40px' }}>
                        {/* Vertical blue line */}
                        <div style={{
                          position: 'absolute',
                          left: '16px',
                          top: '0',
                          bottom: '0',
                          width: '4px',
                          background: 'linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%)',
                          borderRadius: '2px'
                        }}></div>

                        {/* Starting Position — reference point before any actions */}
                        {analysis.combined_actions_impact.current_status && (
                          <div style={{
                            position: 'relative', marginBottom: '40px',
                            background: '#f8fafc', borderRadius: '12px', padding: '20px 24px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '2px solid #cbd5e1'
                          }}>
                            <div style={{
                              position: 'absolute', left: '-32px', top: '20px',
                              width: '32px', height: '32px', background: '#64748b',
                              borderRadius: '50%', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', fontSize: '13px', fontWeight: 'bold', color: 'white'
                            }}>★</div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#475569', marginBottom: '14px' }}>
                              Starting Position — Before Any Recommendations
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                              <div style={{ textAlign: 'center', padding: '10px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current AGI</div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b' }}>
                                  ${(analysis.combined_actions_impact.current_status.agi || 0).toLocaleString()}
                                </div>
                              </div>
                              {analysis.combined_actions_impact.current_status.taxable_income !== undefined && (
                                <div style={{ textAlign: 'center', padding: '10px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Taxable Income</div>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b' }}>
                                    ${(analysis.combined_actions_impact.current_status.taxable_income || 0).toLocaleString()}
                                  </div>
                                </div>
                              )}
                              <div style={{ textAlign: 'center', padding: '10px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tax Bracket</div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc2626' }}>
                                  {analysis.combined_actions_impact.current_status.tax_bracket_label ||
                                   `${Math.round((analysis.combined_actions_impact.current_status.tax_bracket || 0) * 100)}%`}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {analysis.combined_actions_impact.sequential_breakdown.map((step, idx) => (
                          <div key={idx} style={{
                            position: 'relative',
                            marginBottom: idx < analysis.combined_actions_impact.sequential_breakdown.length - 1 ? '40px' : '0',
                            background: 'white',
                            borderRadius: '12px',
                            padding: '24px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: '1px solid #e5e7eb'
                          }}>
                            {/* Circle number on the line */}
                            <div style={{
                              position: 'absolute',
                              left: '-32px',
                              top: '24px',
                              width: '32px',
                              height: '32px',
                              background: 'white',
                              border: '4px solid #3b82f6',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: '#3b82f6'
                            }}>
                              {idx + 1}
                            </div>
                            
                            {/* Action Title */}
                            <h5 style={{
                              fontSize: '20px',
                              fontWeight: 'bold',
                              color: '#1f2937',
                              marginTop: 0,
                              marginBottom: '8px'
                            }}>
                              {step.action_display_title || step.action || step.title}
                            </h5>
                            
                            {/* Savings from this action */}
                            <div style={{
                              fontSize: '15px',
                              color: '#6b7280',
                              marginBottom: '20px'
                            }}>
                              Savings from this action: <span style={{ color: '#059669', fontWeight: '600' }}>
                                ${(step.this_action_savings || step.incremental_savings || step.savings || 
                                   (step.cumulative_savings && idx > 0 && analysis.combined_actions_impact.sequential_breakdown[idx - 1]?.cumulative_savings 
                                     ? step.cumulative_savings - analysis.combined_actions_impact.sequential_breakdown[idx - 1].cumulative_savings 
                                     : step.cumulative_savings) || 0).toLocaleString()}
                              </span>
                            </div>
                            
                            {/* Metrics Grid */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                              gap: '16px'
                            }}>
                              {step.new_agi !== undefined && (
                                <div style={{ textAlign: 'center', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>New AGI</div>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                    ${step.new_agi?.toLocaleString()}
                                  </div>
                                </div>
                              )}
                              
                              {step.new_taxable_income !== undefined && (
                                <div style={{ textAlign: 'center', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>New Taxable Income</div>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                    ${step.new_taxable_income?.toLocaleString()}
                                  </div>
                                </div>
                              )}
                              
                              {step.cumulative_savings !== undefined && (
                                <div style={{ textAlign: 'center', padding: '12px', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #d1fae5' }}>
                                  <div style={{ fontSize: '12px', color: '#065f46', marginBottom: '4px' }}>Cumulative Savings</div>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#059669' }}>
                                    ${step.cumulative_savings?.toLocaleString()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* After All Actions Summary */}
                      {analysis.combined_actions_impact.after_all_actions && (
                        <div style={{
                          marginTop: '40px',
                          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                          border: '2px solid #10b981',
                          borderRadius: '16px',
                          padding: '24px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              background: '#10b981',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '12px'
                            }}>
                              <span style={{ color: 'white', fontSize: '18px' }}>✓</span>
                            </div>
                            <h4 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#065f46' }}>
                              After All Actions
                            </h4>
                          </div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: '16px'
                          }}>
                            {(analysis.combined_actions_impact.after_all_actions.agi || 
                              analysis.combined_actions_impact.after_all_actions.new_agi) && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '4px' }}>New AGI</div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#047857' }}>
                                  ${(analysis.combined_actions_impact.after_all_actions.agi || 
                                     analysis.combined_actions_impact.after_all_actions.new_agi)?.toLocaleString()}
                                </div>
                              </div>
                            )}
                            
                            {(analysis.combined_actions_impact.after_all_actions.taxable_income || 
                              analysis.combined_actions_impact.after_all_actions.new_taxable_income) && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '4px' }}>New Taxable Income</div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#047857' }}>
                                  ${(analysis.combined_actions_impact.after_all_actions.taxable_income || 
                                     analysis.combined_actions_impact.after_all_actions.new_taxable_income)?.toLocaleString()}
                                </div>
                              </div>
                            )}
                            
                            {(analysis.combined_actions_impact.after_all_actions.tax_bracket || 
                              analysis.combined_actions_impact.after_all_actions.new_tax_bracket ||
                              analysis.combined_actions_impact.after_all_actions.marginal_rate) && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '4px' }}>New Tax Bracket</div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#047857' }}>
                                  {(() => {
                                    const bracket = analysis.combined_actions_impact.after_all_actions.tax_bracket || 
                                                   analysis.combined_actions_impact.after_all_actions.new_tax_bracket ||
                                                   analysis.combined_actions_impact.after_all_actions.marginal_rate;
                                    // If it's a decimal (like 0.35), convert to percentage
                                    return bracket < 1 ? Math.round(bracket * 100) : Math.round(bracket);
                                  })()}%
                                </div>
                              </div>
                            )}
                            
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '4px' }}>Total Savings</div>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#047857' }}>
                                ${(() => {
                                  // Try to get the last cumulative savings from sequential_breakdown
                                  if (analysis.combined_actions_impact.sequential_breakdown && 
                                      analysis.combined_actions_impact.sequential_breakdown.length > 0) {
                                    const lastStep = analysis.combined_actions_impact.sequential_breakdown[
                                      analysis.combined_actions_impact.sequential_breakdown.length - 1
                                    ];
                                    return (lastStep.cumulative_savings || 0).toLocaleString();
                                  }
                                  // Fallback to other fields
                                  return (analysis.combined_actions_impact.total_impact?.total_tax_savings || 
                                          analysis.combined_actions_impact.total_savings ||
                                          analysis.potential_savings || 0).toLocaleString();
                                })()}
                              </div>
                            </div>
                          </div>
                          
                          {/* Tax bracket change message - ALWAYS show if we have data */}
                          {analysis.combined_actions_impact.current_status && analysis.combined_actions_impact.after_all_actions && (
                            (() => {
                              // Use ACTUAL backend field names!
                              const currentBracket = analysis.combined_actions_impact.current_status.tax_bracket;
                              const newBracket = analysis.combined_actions_impact.after_all_actions.new_tax_bracket;
                              
                              if (currentBracket === undefined || newBracket === undefined) return null;
                              
                              // Convert to percentage if decimal
                              const currentPct = currentBracket < 1 ? Math.round(currentBracket * 100) : Math.round(currentBracket);
                              const newPct = newBracket < 1 ? Math.round(newBracket * 100) : Math.round(newBracket);
                              
                              // Show message
                              return (
                                <div style={{
                                  marginTop: '16px',
                                  padding: '12px',
                                  background: 'white',
                                  borderRadius: '8px',
                                  textAlign: 'center',
                                  fontSize: '15px',
                                  color: '#065f46',
                                  fontWeight: '600'
                                }}>
                                  ✨ Tax bracket {currentPct > newPct ? 'drops' : (currentPct < newPct ? 'increases' : 'stays at')} from {currentPct}% to {newPct}%
                                </div>
                              );
                            })()
                          )}
                          
                          {/* Bonus from bracket optimization — always use server-calculated values */}
                          {(() => {
                            const totalImpact = analysis.combined_actions_impact?.total_impact;
                            // Server sends: bonus_from_bracket_optimization and simple_sum_of_individual_savings
                            // These are the authoritative values — never recalculate in the frontend
                            const bracketBonus = totalImpact?.bonus_from_bracket_optimization;
                            const simpleSum    = totalImpact?.simple_sum_of_individual_savings;

                            // Only render if server sent both values
                            if (bracketBonus === undefined || bracketBonus === null ||
                                simpleSum    === undefined || simpleSum    === null) {
                              return null;
                            }

                            const bracketDropped = analysis.combined_actions_impact?.after_all_actions?.bracket_dropped;
                            const bonusRounded   = Math.round(bracketBonus);
                            const simpleSumRounded = Math.round(simpleSum);

                            // Determine message based on whether bracket actually dropped
                            let messageText;
                            if (!bracketDropped && bonusRounded === 0) {
                              // No bracket change and no difference — bracket stayed same, no effect
                              messageText = (
                                <>
                                  Bracket stays the same — sequential savings equal the flat-rate estimate (
                                  <strong style={{ color: '#78350f' }}>${simpleSumRounded.toLocaleString()}</strong>
                                  ). No bracket compression effect.
                                </>
                              );
                            } else if (bracketBonus < 0) {
                              // Bracket dropped — deductions hit the lower bracket, worth less than flat estimate
                              messageText = (
                                <>
                                  Tax bracket drops during optimization — deductions hitting the lower bracket
                                  reduce savings by{' '}
                                  <strong style={{ color: '#78350f' }}>${Math.abs(bonusRounded).toLocaleString()}</strong>
                                  {' '}vs the flat-rate estimate (${simpleSumRounded.toLocaleString()}).
                                  Sequential total is the accurate figure.
                                </>
                              );
                            } else if (bracketBonus > 0) {
                              // Positive bonus — sequential saves MORE than flat-rate estimate
                              messageText = (
                                <>
                                  By implementing sequentially, you save an additional{' '}
                                  <strong style={{ color: '#78350f' }}>${bonusRounded.toLocaleString()}</strong>
                                  {' '}beyond the flat-rate estimate (${simpleSumRounded.toLocaleString()})
                                  due to progressive bracket optimization!
                                </>
                              );
                            } else {
                              // Zero bonus — sequential equals simple sum exactly
                              messageText = (
                                <>
                                  Sequential savings match the flat-rate estimate (
                                  <strong style={{ color: '#78350f' }}>${simpleSumRounded.toLocaleString()}</strong>
                                  ) — no bracket compression effect.
                                </>
                              );
                            }

                            return (
                              <div style={{
                                marginTop: '12px',
                                padding: '16px',
                                background: '#fffbeb',
                                border: '2px solid #fbbf24',
                                borderRadius: '8px'
                              }}>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#78350f', marginBottom: '8px' }}>
                                  💡 Tax Bracket Optimization Impact
                                </div>
                                <div style={{ fontSize: '14px', color: '#92400e', lineHeight: '1.5' }}>
                                  {messageText}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ══════════════════════════════════════════════════════ */}
              {/* TAX OUTCOME SUMMARY — "What does this mean for my bill?" */}
              {/* ══════════════════════════════════════════════════════ */}
              {analysis.combined_actions_impact?.tax_outcome && (
                <div style={{
                  marginTop: '30px',
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)',
                  borderRadius: '16px',
                  padding: '28px',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(30,58,95,0.25)'
                }}>
                  {/* Header */}
                  <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '6px', letterSpacing: '-0.3px' }}>
                    💰 Your Tax Outcome
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '24px' }}>
                    {analysis.combined_actions_impact.tax_outcome.summary}
                  </div>

                  {/* Before / After Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>

                    {/* BEFORE box */}
                    <div style={{
                      background: 'rgba(255,255,255,0.10)',
                      borderRadius: '12px',
                      padding: '18px',
                      border: '1px solid rgba(255,255,255,0.15)'
                    }}>
                      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.7, marginBottom: '12px' }}>
                        Before Recommendations
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '11px', opacity: 0.7 }}>Total Tax (Line 24)</div>
                        <div style={{ fontSize: '22px', fontWeight: '700' }}>
                          ${(analysis.combined_actions_impact.tax_outcome.original_total_tax || 0).toLocaleString()}
                        </div>
                      </div>
                      {analysis.combined_actions_impact.tax_outcome.total_payments > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '11px', opacity: 0.7 }}>Withholding + Payments</div>
                          <div style={{ fontSize: '16px', fontWeight: '600' }}>
                            ${(analysis.combined_actions_impact.tax_outcome.total_payments || 0).toLocaleString()}
                          </div>
                        </div>
                      )}
                      <div style={{
                        marginTop: '12px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        background: analysis.combined_actions_impact.tax_outcome.original_refund > 0
                          ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)',
                        border: `1px solid ${analysis.combined_actions_impact.tax_outcome.original_refund > 0
                          ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`
                      }}>
                        <div style={{ fontSize: '11px', opacity: 0.85 }}>
                          {analysis.combined_actions_impact.tax_outcome.original_refund > 0 ? 'Refund' : 'Balance Due'}
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '700' }}>
                          ${(analysis.combined_actions_impact.tax_outcome.original_refund > 0
                            ? analysis.combined_actions_impact.tax_outcome.original_refund
                            : analysis.combined_actions_impact.tax_outcome.original_balance_due || 0
                          ).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {/* AFTER box */}
                    <div style={{
                      background: 'rgba(255,255,255,0.15)',
                      borderRadius: '12px',
                      padding: '18px',
                      border: '2px solid rgba(16,185,129,0.6)'
                    }}>
                      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.7, marginBottom: '12px' }}>
                        After All Recommendations ✓
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '11px', opacity: 0.7 }}>Estimated Tax</div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: '#6ee7b7' }}>
                          ${(analysis.combined_actions_impact.tax_outcome.estimated_new_tax || 0).toLocaleString()}
                        </div>
                      </div>
                      {analysis.combined_actions_impact.tax_outcome.total_payments > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '11px', opacity: 0.7 }}>Withholding + Payments</div>
                          <div style={{ fontSize: '16px', fontWeight: '600' }}>
                            ${(analysis.combined_actions_impact.tax_outcome.total_payments || 0).toLocaleString()}
                          </div>
                        </div>
                      )}
                      <div style={{
                        marginTop: '12px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        background: analysis.combined_actions_impact.tax_outcome.estimated_refund > 0
                          ? 'rgba(16,185,129,0.35)' : 'rgba(255,255,255,0.12)',
                        border: `1px solid ${analysis.combined_actions_impact.tax_outcome.estimated_refund > 0
                          ? 'rgba(16,185,129,0.6)' : 'rgba(255,255,255,0.2)'}`
                      }}>
                        <div style={{ fontSize: '11px', opacity: 0.85 }}>
                          {analysis.combined_actions_impact.tax_outcome.estimated_refund > 0 ? 'Estimated Refund' : 'Estimated Balance Due'}
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#6ee7b7' }}>
                          ${(analysis.combined_actions_impact.tax_outcome.estimated_refund > 0
                            ? analysis.combined_actions_impact.tax_outcome.estimated_refund
                            : analysis.combined_actions_impact.tax_outcome.estimated_balance_due || 0
                          ).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Savings banner */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    background: 'rgba(16,185,129,0.2)',
                    border: '1px solid rgba(16,185,129,0.4)',
                    borderRadius: '10px',
                    padding: '14px 20px'
                  }}>
                    <span style={{ fontSize: '22px' }}>🎯</span>
                    <div>
                      <span style={{ fontSize: '14px', opacity: 0.85 }}>Total Tax Savings: </span>
                      <span style={{ fontSize: '22px', fontWeight: '800', color: '#6ee7b7' }}>
                        ${(analysis.combined_actions_impact.tax_outcome.total_savings || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div style={{ fontSize: '11px', opacity: 0.55, marginTop: '12px', textAlign: 'center' }}>
                    * Estimated figures based on implementing all recommendations. Actual results may vary. Consult your CPA.
                  </div>
                </div>
              )}

              {/* Items Reviewed with $0 Savings */}
              {showZeroSavings && analysis.items_reviewed_no_savings && analysis.items_reviewed_no_savings.length > 0 && (
                <div style={{ marginTop: '30px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#1f2937' }}>
                    ✅ Items Reviewed (No Action Needed)
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {analysis.items_reviewed_no_savings.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '16px',
                          background: '#f9fafb'
                        }}
                      >
                        {typeof item === 'string' ? (
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            {item}
                          </div>
                        ) : (
                          <>
                            <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>
                              {item.title}
                            </div>
                            <div style={{ fontSize: '14px', color: '#6b7280' }}>
                              {item.result}
                            </div>
                            {item.note && (
                              <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px', fontStyle: 'italic' }}>
                                {item.note}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center', color: 'rgba(255,255,255,0.45)',
          fontSize: '13px', marginTop: '8px'
        }}>
          <p style={{ margin: 0 }}>© 2026 TaxSavvy.ai · Built by CPAs · Your data is never stored · Confidential & Proprietary</p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media print {
          body { background: white !important; }
          nav, button, .no-print { display: none !important; }
          .App { background: white !important; padding: 0 !important; }
          div[style*="background: linear-gradient(135deg, #667eea"] { 
            background: #667eea !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;
          }
          * { box-shadow: none !important; }
        }
      `}</style>
    </div>
      )}
    </div>
  );
}

export default App;
